import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
  catalogueItemApi,
  cataloguePhotoApi,
  catalogueSectionApi,
  pageLayoutApi,
  productApi,
  toAbsUrl,
} from "../api/api";
import "./common.css";
import { useResizableColumns } from "../hooks/useResizableColumns";
import SearchableDropdown from "../components/SearchableDropdown";
import RvDataCard, { RvDataCardList } from "../components/RvDataCard";

/* ══════════════════════════════════════
   SHARED MICRO-COMPONENTS
══════════════════════════════════════ */

function Toast({ message, type, onClose }) {
  useEffect(() => { const t = setTimeout(onClose, 3400); return () => clearTimeout(t); }, [onClose]);
  return (
    <div className={`rv-toast rv-toast--${type}`}>
      <span className="rv-toast-icon">{type === "success" ? <CheckIcon /> : <AlertIcon />}</span>
      <span className="rv-toast-msg">{message}</span>
      <button className="rv-toast-close" onClick={onClose}><CloseIcon /></button>
    </div>
  );
}

function ConfirmDialog({ name, onConfirm, onCancel }) {
  return (
    <div className="rv-dialog-backdrop">
      <div className="rv-dialog">
        <div className="rv-dialog-icon"><TrashIcon /></div>
        <h3 className="rv-dialog-title">Delete Item?</h3>
        <p className="rv-dialog-desc"><strong>{name}</strong> and all its photos will be permanently removed.</p>
        <div className="rv-dialog-actions">
          <button className="rv-btn rv-btn--ghost" onClick={onCancel}>Cancel</button>
          <button className="rv-btn rv-btn--danger" onClick={onConfirm}>Delete</button>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════
   PHOTO MANAGER (per catalogue item)
══════════════════════════════════════ */
function PhotoManager({ itemID, photos, onPhotosChange, showToast }) {
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [editPhoto, setEditPhoto] = useState(null);
  const fileRef = useRef();

  /* ── Photo src resolver ── */
  const getPhotoSrc = (ph) => {
    if (!ph) return null;
    const rawPath =
      ph.filepath ||
      ph.Filepath ||
      ph.filename ||
      ph.Filename ||
      (Array.isArray(ph.photos) ? ph.photos[0] : ph.photos) ||
      (Array.isArray(ph.Photos) ? ph.Photos[0] : ph.Photos);
    return rawPath && typeof rawPath === "string" ? toAbsUrl(rawPath) : null;
  };

  /* ── Upload new photos ── */
  const handleFiles = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      await cataloguePhotoApi.upload({
        catalogueItemID: itemID,
        files,
        comments: "",
        displayOrder: photos.length + 1,
        isPrimary: photos.length === 0,
      });
      showToast(`${files.length} photo(s) uploaded.`);
      onPhotosChange();
    } catch (err) {
      showToast(err.message || "Upload failed.", "error");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  /* ── Delete photo ── */
  const handleDeletePhoto = async (photoID) => {
    setDeleting(photoID);
    try {
      await cataloguePhotoApi.remove(photoID);
      showToast("Photo removed.");
      onPhotosChange();
    } catch (err) {
      showToast(err.message || "Delete failed.", "error");
    } finally {
      setDeleting(null);
    }
  };

  /* ── Update photo meta ── */
  const handleUpdatePhoto = async () => {
    if (!editPhoto) return;
    try {
      await cataloguePhotoApi.update({
        photoID: editPhoto.photo.photoID,
        catalogueItemID: itemID,
        filepath: editPhoto.photo.filepath ?? "",
        filename: editPhoto.photo.filename ?? "",
        comments: editPhoto.form.comments,
        displayOrder: Number(editPhoto.form.displayOrder),
        isPrimary: editPhoto.form.isPrimary,
      });
      showToast("Photo updated.");
      setEditPhoto(null);
      onPhotosChange();
    } catch (err) {
      showToast(err.message || "Update failed.", "error");
    }
  };

  return (
    <div className="rv-pm">
      {/* Upload zone */}
      <div
        className={`rv-pm-drop ${uploading ? "rv-pm-drop--loading" : ""}`}
        onClick={() => !uploading && fileRef.current?.click()}
        onDragOver={e => e.preventDefault()}
        onDrop={e => { e.preventDefault(); handleFiles(e.dataTransfer.files); }}
      >
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          multiple
          style={{ display: "none" }}
          onChange={e => handleFiles(e.target.files)}
        />
        {uploading ? (
          <><div className="rv-pm-spinner" /><span>Uploading…</span></>
        ) : (
          <><UploadIcon /><span>Drop images here or <strong>click to browse</strong></span></>
        )}
      </div>

      {/* Photo grid */}
      {photos.length === 0 ? (
        <p className="rv-pm-empty">No photos yet. Upload above to get started.</p>
      ) : (
        <div className="rv-pm-grid">
          {photos
            .slice()
            .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
            .map(ph => (
              <div key={ph.photoID} className={`rv-pm-thumb ${ph.isPrimary ? "rv-pm-thumb--primary" : ""}`}>

                {/* Image preview */}
                {getPhotoSrc(ph) ? (
                  <img
                    src={getPhotoSrc(ph)}
                    alt={ph.filename || "photo"}
                    className="rv-pm-img"
                    onError={e => { e.target.style.display = "none"; }}
                  />
                ) : (
                  <div className="rv-pm-img-placeholder"><ImageIcon /></div>
                )}

                {/* Badges */}
                <div className="rv-pm-badges">
                  {ph.isPrimary && <span className="rv-pm-badge rv-pm-badge--primary">Primary</span>}
                  <span className="rv-pm-badge rv-pm-badge--order">#{ph.displayOrder ?? "—"}</span>
                </div>

                {/* Actions */}
                <div className="rv-pm-actions">
                  <button
                    className="rv-pm-btn rv-pm-btn--edit"
                    title="Edit"
                    onClick={() => setEditPhoto({
                      photo: ph,
                      form: {
                        comments: ph.comments ?? "",
                        displayOrder: ph.displayOrder ?? 1,
                        isPrimary: ph.isPrimary ?? false,
                      },
                    })}
                  ><EditIcon /></button>
                  <button
                    className="rv-pm-btn rv-pm-btn--del"
                    title="Delete"
                    disabled={deleting === ph.photoID}
                    onClick={() => handleDeletePhoto(ph.photoID)}
                  >
                    {deleting === ph.photoID ? <div className="rv-pm-mini-spin" /> : <TrashIcon />}
                  </button>
                </div>

                {ph.comments && <p className="rv-pm-caption">{ph.comments}</p>}
              </div>
            ))}
        </div>
      )}

      {/* Edit photo modal */}
      {editPhoto && (
        <div className="rv-dialog-backdrop">
          <div className="rv-dialog rv-dialog--form rv-dialog--sm">
            <div className="rv-modal-header">
              <div className="rv-modal-icon"><ImageIcon /></div>
              <div>
                <h3 className="rv-modal-title">Edit Photo</h3>
                <p className="rv-modal-sub">{editPhoto.photo.filename || `Photo #${editPhoto.photo.photoID}`}</p>
              </div>
              <button className="rv-modal-close" onClick={() => setEditPhoto(null)}><CloseIcon /></button>
            </div>
            <div className="rv-modal-body">
              <div className="rv-field">
                <label className="rv-label">Comments / Caption</label>
                <input
                  className="rv-input"
                  placeholder="e.g. Front view"
                  value={editPhoto.form.comments}
                  onChange={e => setEditPhoto(p => ({ ...p, form: { ...p.form, comments: e.target.value } }))}
                />
              </div>
              <div className="rv-field-row-2col">
                <div className="rv-field">
                  <label className="rv-label">Display Order</label>
                  <input
                    className="rv-input"
                    type="number"
                    min="1"
                    value={editPhoto.form.displayOrder}
                    onChange={e => setEditPhoto(p => ({ ...p, form: { ...p.form, displayOrder: e.target.value } }))}
                  />
                </div>
                <div className="rv-field rv-field--row">
                  <div>
                    <label className="rv-label">Primary Photo</label>
                    <p className="rv-field-hint">Shown as cover image</p>
                  </div>
                  <button
                    type="button"
                    className={`rv-toggle ${editPhoto.form.isPrimary ? "rv-toggle--on" : ""}`}
                    onClick={() => setEditPhoto(p => ({ ...p, form: { ...p.form, isPrimary: !p.form.isPrimary } }))}
                  ><span className="rv-toggle-knob" /></button>
                </div>
              </div>
            </div>
            <div className="rv-modal-footer">
              <button className="rv-btn rv-btn--ghost" onClick={() => setEditPhoto(null)}>Cancel</button>
              <button className="rv-btn rv-btn--primary" onClick={handleUpdatePhoto}>Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════
   RICH TEXT EDITOR (QUILL) FOR CATALOGUE
══════════════════════════════════════ */
function CatalogueQuillEditor({ initialHtml, onChange, placeholder }) {
  const containerRef = useRef(null);
  const quillRef = useRef(null);
  const onChangeRef = useRef(onChange);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    if (!containerRef.current || quillRef.current) return;
    if (!window.Quill) {
      console.error("Quill not loaded — ensure CDN links are present in public/index.html.");
      return;
    }

    const quill = new window.Quill(containerRef.current, {
      theme: "snow",
      placeholder: placeholder || "Write item description here…",
      modules: {
        toolbar: [
          [{ size: ["small", false, "large", "huge"] }],
          [{ header: [1, 2, 3, false] }],
          ["bold", "italic", "underline", "strike"],
          [{ color: [] }, { background: [] }],
          [{ align: [] }],
          [{ list: "ordered" }, { list: "bullet" }],
          ["blockquote"],
          ["link"],
          ["clean"],
        ],
      },
    });

    if (initialHtml && initialHtml.trim() && initialHtml !== "<p><br></p>") {
      if (/<[a-z][\s\S]*>/i.test(initialHtml)) {
        quill.clipboard.dangerouslyPasteHTML(initialHtml);
      } else {
        quill.setText(initialHtml.trim() + "\n");
      }
    }

    quill.on("text-change", () => {
      const isEmpty = quill.getText().trim().length === 0;
      const html = isEmpty ? "" : quill.root.innerHTML;
      if (onChangeRef.current) {
        onChangeRef.current(html);
      }
    });

    quillRef.current = quill;

    return () => {
      const toolbar = quill.getModule("toolbar");
      if (toolbar?.container) toolbar.container.remove();
      if (containerRef.current) containerRef.current.innerHTML = "";
      quillRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      ref={containerRef}
      style={{
        minHeight: 140,
        fontSize: 13.5,
        fontFamily: "var(--b-font, sans-serif)",
      }}
    />
  );
}

/* ══════════════════════════════════════
   CATALOGUE ITEM FORM MODAL
══════════════════════════════════════ */
function CatalogueModal({ initial, products, sections = [], onSave, onClose, saving, showToast }) {
  const isEdit = !!initial?.catalogueItemID;

  const [form, setForm] = useState({
    productID: initial?.productID != null ? String(initial.productID) : "",
    displayName: initial?.displayName ?? "",
    descriptionLbl: initial?.descriptionLbl ?? "",
    description: initial?.description ?? "",
    comments: initial?.comments ?? "",
    displaySection: initial?.displaySection ?? "",
    isFeatured: initial?.isFeatured ?? false,
    isActive: initial?.isActive ?? true,
  });
  const [errors, setErrors] = useState({});
  const [showSectionDropdown, setShowSectionDropdown] = useState(false);
  const sectionComboboxRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (sectionComboboxRef.current && !sectionComboboxRef.current.contains(e.target)) {
        setShowSectionDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredSections = useMemo(() => {
    const q = (form.displaySection || "").trim().toLowerCase();
    if (!q) return sections;
    return sections.filter((s) =>
      (s.publicTitle || s.title || "").toLowerCase().includes(q)
    );
  }, [sections, form.displaySection]);

  const [pendingFiles, setPendingFiles] = useState([]);
  const [existingPhotos, setExistingPhotos] = useState([]);
  const [loadingPhotos, setLoadingPhotos] = useState(false);
  const fileInputRef = useRef();

  useEffect(() => {
    if (isEdit && initial?.catalogueItemID) {
      setLoadingPhotos(true);
      cataloguePhotoApi.getByItemId(initial.catalogueItemID)
        .then(data => setExistingPhotos(Array.isArray(data) ? data : data?.data ?? []))
        .catch(() => setExistingPhotos([]))
        .finally(() => setLoadingPhotos(false));
    }
  }, [isEdit, initial?.catalogueItemID]);

  const set = (key, val) => {
    setForm(f => ({ ...f, [key]: val }));
    setErrors(e => ({ ...e, [key]: "" }));
  };

  const handleFileSelect = (files) => {
    if (!files || !files.length) return;
    const newFiles = Array.from(files);
    setPendingFiles(prev => [...prev, ...newFiles]);
  };

  const removePendingFile = (idx) => {
    setPendingFiles(prev => prev.filter((_, i) => i !== idx));
  };

  const handleDeleteExisting = async (photoID) => {
    try {
      await cataloguePhotoApi.remove(photoID);
      setExistingPhotos(prev => prev.filter(p => p.photoID !== photoID));
      if (showToast) showToast("Photo removed.");
    } catch (err) {
      if (showToast) showToast(err.message || "Failed to remove photo.", "error");
    }
  };

  const validate = () => {
    const e = {};
    if (!form.productID) e.productID = "Please select a product.";
    if (!form.displayName.trim()) e.displayName = "Display name is required.";
    return e;
  };

  const handleSubmit = () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    onSave(
      {
        ...initial,
        productID: Number(form.productID),
        displayName: form.displayName.trim(),
        descriptionLbl: form.descriptionLbl.trim(),
        description: form.description === "<p><br></p>" ? "" : form.description.trim(),
        comments: form.comments.trim(),
        displaySection: form.displaySection.trim(),
        isFeatured: form.isFeatured,
        isActive: form.isActive,
      },
      pendingFiles
    );
  };

  return (
    <div className="rv-dialog-backdrop">
      <div className="rv-dialog rv-dialog--form rv-dialog--wide" style={{ maxWidth: 720, maxHeight: "90vh", display: "flex", flexDirection: "column" }}>
        <div className="rv-modal-header">
          <div className="rv-modal-icon"><CatalogueIcon /></div>
          <div>
            <h3 className="rv-modal-title">{isEdit ? "Edit Catalogue Item" : "New Catalogue Item"}</h3>
            <p className="rv-modal-sub">
              {isEdit ? "Update catalogue details and photos below." : "Fill in details and upload photos to add a product to the catalogue."}
            </p>
          </div>
          <button className="rv-modal-close" onClick={onClose}><CloseIcon /></button>
        </div>

        <div className="rv-modal-body" style={{ overflowY: "auto", flex: 1, paddingRight: 16 }}>

          {/* Product */}
          <div className="rv-field">
            <label className="rv-label">Product <span className="rv-required">*</span></label>
            <SearchableDropdown
              options={products.map(p => ({
                ...p,
                label: p.productCode ? `${p.productName} (${p.productCode})` : p.productName
              }))}
              valueKey="productID"
              labelKey="label"
              value={form.productID}
              onChange={val => set("productID", val)}
              placeholder="Select product…"
              searchPlaceholder="Search products…"
              error={errors.productID}
              pageSize={10}
            />
          </div>

          {/* Display name + section */}
          <div className="rv-field-row-2col">
            <div className="rv-field">
              <label className="rv-label">Display Name <span className="rv-required">*</span></label>
              <input
                className={`rv-input ${errors.displayName ? "rv-input--err" : ""}`}
                placeholder="e.g. Royal Solitaire Ring"
                value={form.displayName}
                onChange={e => set("displayName", e.target.value)}
              />
              {errors.displayName && <p className="rv-field-err">{errors.displayName}</p>}
            </div>
            <div className="rv-field" ref={sectionComboboxRef}>
              <label className="rv-label">Display Section</label>
              <div style={{ position: "relative" }}>
                <input
                  className="rv-input"
                  placeholder="Select or type a section name…"
                  value={form.displaySection}
                  onChange={e => {
                    set("displaySection", e.target.value);
                    setShowSectionDropdown(true);
                  }}
                  onFocus={() => setShowSectionDropdown(true)}
                />

                {showSectionDropdown && filteredSections.length > 0 && (
                  <div
                    style={{
                      position: "absolute",
                      top: "calc(100% + 4px)",
                      left: 0,
                      right: 0,
                      zIndex: 999,
                      background: "#fff",
                      border: "1px solid rgba(185,167,146,0.35)",
                      borderRadius: 8,
                      boxShadow: "0 4px 16px rgba(0,0,0,0.10)",
                      maxHeight: 200,
                      overflowY: "auto",
                    }}
                  >
                    {filteredSections.map((sec, i) => {
                      const title = sec.publicTitle || sec.title;
                      return (
                        <div
                          key={sec.sectionID || i}
                          onClick={() => {
                            set("displaySection", title);
                            setShowSectionDropdown(false);
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = "rgba(185,167,146,0.10)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = "#fff";
                          }}
                          style={{
                            padding: "9px 14px",
                            fontSize: 13,
                            color: "#332F2F",
                            cursor: "pointer",
                            borderBottom: i < filteredSections.length - 1 ? "1px solid rgba(185,167,146,0.1)" : "none",
                            background: "#fff",
                            transition: "background 0.15s ease",
                          }}
                        >
                          {title}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {form.displaySection.trim() && !sections.some(s => (s.publicTitle || s.title || "").toLowerCase() === form.displaySection.trim().toLowerCase()) && (
                <p style={{ fontSize: 11, color: "#8B6508", margin: "4px 0 0", display: "flex", alignItems: "center", gap: 4 }}>
                  ⚡ New section will be auto-created as a Draft when saved.
                </p>
              )}
            </div>
          </div>

          {/* Photo Upload Zone */}
          <div className="rv-field" style={{ marginBottom: 14 }}>
            <label className="rv-label" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span>Photos & Gallery</span>
              <span style={{ fontSize: 11, color: "var(--taupe)", fontWeight: 400 }}>Supports JPG, PNG, WEBP</span>
            </label>

            <div
              className="rv-pm-drop"
              onClick={() => fileInputRef.current?.click()}
              onDragOver={e => e.preventDefault()}
              onDrop={e => {
                e.preventDefault();
                handleFileSelect(e.dataTransfer.files);
              }}
              style={{ cursor: "pointer", border: "1.5px dashed var(--taupe)", borderRadius: 10, padding: "16px 12px", textAlign: "center", background: "rgba(185,167,146,.05)" }}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                style={{ display: "none" }}
                onChange={e => handleFileSelect(e.target.files)}
              />
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, color: "var(--dark)" }}>
                <UploadIcon />
                <span style={{ fontSize: 12.5 }}>
                  Drop photos here or <strong style={{ color: "var(--taupe-dark)" }}>click to browse</strong>
                </span>
              </div>
            </div>

            {/* Selected Pending Photos Preview */}
            {pendingFiles.length > 0 && (
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 10 }}>
                {pendingFiles.map((file, idx) => (
                  <div key={idx} style={{ position: "relative", width: 64, height: 64, borderRadius: 8, overflow: "hidden", border: "1px solid var(--taupe)" }}>
                    <img
                      src={URL.createObjectURL(file)}
                      alt="preview"
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                    <button
                      type="button"
                      onClick={() => removePendingFile(idx)}
                      style={{
                        position: "absolute", top: 2, right: 2, background: "rgba(0,0,0,.6)", color: "#fff",
                        border: "none", borderRadius: "50%", width: 18, height: 18, fontSize: 10,
                        display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer"
                      }}
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Existing Photos when editing */}
            {isEdit && existingPhotos.length > 0 && (
              <div style={{ marginTop: 10 }}>
                <p style={{ fontSize: 11, color: "var(--taupe)", marginBottom: 6, fontWeight: 600 }}>Existing Uploaded Photos:</p>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {existingPhotos.map(ph => {
                    const rawSrc =
                      ph.filepath ||
                      ph.Filepath ||
                      ph.filename ||
                      ph.Filename ||
                      (Array.isArray(ph.photos) ? ph.photos[0] : ph.photos) ||
                      (Array.isArray(ph.Photos) ? ph.Photos[0] : ph.Photos);
                    const src = rawSrc && typeof rawSrc === "string" ? toAbsUrl(rawSrc) : null;
                    return (
                      <div key={ph.photoID} style={{ position: "relative", width: 64, height: 64, borderRadius: 8, overflow: "hidden", border: "1px solid var(--taupe)" }}>
                        {src ? (
                          <img src={src} alt="existing" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                        ) : (
                          <div style={{ width: "100%", height: "100%", background: "#eee", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10 }}>No img</div>
                        )}
                        <button
                          type="button"
                          onClick={() => handleDeleteExisting(ph.photoID)}
                          title="Delete photo"
                          style={{
                            position: "absolute", top: 2, right: 2, background: "rgba(192,57,43,.85)", color: "#fff",
                            border: "none", borderRadius: "50%", width: 18, height: 18, fontSize: 10,
                            display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer"
                          }}
                        >
                          ✕
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Short label */}
          <div className="rv-field">
            <label className="rv-label">Description Label</label>
            <input
              className="rv-input"
              placeholder="Short label shown under the item (e.g. 18K Yellow Gold · 0.50 ct Diamond)"
              value={form.descriptionLbl}
              onChange={e => set("descriptionLbl", e.target.value)}
            />
          </div>

          {/* Full description (Rich Text) */}
          <div className="rv-field">
            <label className="rv-label" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span>Description</span>
              <span style={{ fontSize: 11, color: "var(--taupe)", fontWeight: 400 }}>Rich Text (Colors, Highlights, Font Size, Alignment)</span>
            </label>
            <div style={{
              background: "#fff",
              borderRadius: 8,
              border: "1px solid rgba(185,167,146,.35)",
              overflow: "hidden",
            }}>
              <CatalogueQuillEditor
                initialHtml={form.description}
                onChange={html => set("description", html)}
                placeholder="Full description for the catalogue listing — features, purity details, styling notes…"
              />
            </div>
          </div>

          {/* Comments */}
          <div className="rv-field">
            <label className="rv-label">Internal Comments</label>
            <input
              className="rv-input"
              placeholder="Internal notes (not shown publicly)"
              value={form.comments}
              onChange={e => set("comments", e.target.value)}
            />
          </div>

          {/* Toggles */}
          <div className="rv-toggle-row">
            <div className="rv-field rv-field--row">
              <div>
                <label className="rv-label">Featured</label>
                <p className="rv-field-hint">Highlight this item prominently with star</p>
              </div>
              <button
                type="button"
                className={`rv-toggle ${form.isFeatured ? "rv-toggle--on" : ""}`}
                onClick={() => set("isFeatured", !form.isFeatured)}
              ><span className="rv-toggle-knob" /></button>
            </div>
            <div className="rv-field rv-field--row">
              <div>
                <label className="rv-label">Active</label>
                <p className="rv-field-hint">Visible in public customer catalogue</p>
              </div>
              <button
                type="button"
                className={`rv-toggle ${form.isActive ? "rv-toggle--on" : ""}`}
                onClick={() => set("isActive", !form.isActive)}
              ><span className="rv-toggle-knob" /></button>
            </div>
          </div>

        </div>

        <div className="rv-modal-footer">
          <button className="rv-btn rv-btn--ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="rv-btn rv-btn--primary" onClick={handleSubmit} disabled={saving}>
            {saving ? <span className="rv-spinner" /> : null}
            {isEdit ? "Update Item" : "Create Item & Upload"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════
   CATALOGUE ITEM FULL VIEW MODAL
══════════════════════════════════════ */
function CatalogueDetailModal({ item, products, onEdit, onManagePhotos, onClose, showToast }) {
  const [photos, setPhotos] = useState([]);
  const [loadingPhotos, setLoadingPhotos] = useState(true);
  const [activePhotoIdx, setActivePhotoIdx] = useState(0);

  const product = products.find(p => Number(p.productID) === Number(item.productID));

  useEffect(() => {
    if (!item?.catalogueItemID) return;
    setLoadingPhotos(true);
    cataloguePhotoApi.getByItemId(item.catalogueItemID)
      .then(res => {
        const list = Array.isArray(res) ? res : res?.data ?? [];
        setPhotos(list);
      })
      .catch(() => setPhotos([]))
      .finally(() => setLoadingPhotos(false));
  }, [item?.catalogueItemID]);

  const getPhotoSrc = (ph) => {
    if (!ph) return null;
    const rawPath =
      ph.filepath ||
      ph.Filepath ||
      ph.filename ||
      ph.Filename ||
      (Array.isArray(ph.photos) ? ph.photos[0] : ph.photos) ||
      (Array.isArray(ph.Photos) ? ph.Photos[0] : ph.Photos);
    return rawPath && typeof rawPath === "string" ? toAbsUrl(rawPath) : null;
  };

  const activePhoto = photos[activePhotoIdx] || photos.find(p => p.isPrimary) || photos[0];
  const activeSrc = getPhotoSrc(activePhoto);

  return (
    <div className="rv-dialog-backdrop" onClick={onClose} style={{ zIndex: 500 }}>
      <div
        className="rv-dialog rv-dialog--form rv-dialog--wide rv-cat-detail-modal"
        style={{
          maxWidth: 880,
          width: "100%",
          maxHeight: "92vh",
          display: "flex",
          flexDirection: "column",
          borderRadius: 14,
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="rv-modal-header" style={{ alignItems: "center" }}>
          <div className="rv-modal-icon" style={{ background: "rgba(185,167,146,.2)", color: "var(--taupe-dark)" }}>
            <CatalogueIcon />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <h3 className="rv-modal-title" style={{ margin: 0, fontSize: 18 }}>{item.displayName}</h3>
              {item.isFeatured && (
                <span className="rv-featured-badge"><StarIcon /> Featured</span>
              )}
              <span style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "3.5px 10px",
                borderRadius: "20px",
                fontSize: "11px",
                fontWeight: 600,
                letterSpacing: "0.02em",
                background: item.isActive ? "rgba(16, 185, 129, 0.09)" : "rgba(239, 68, 68, 0.08)",
                color: item.isActive ? "#065F46" : "#991B1B",
                border: item.isActive ? "1px solid rgba(16, 185, 129, 0.28)" : "1px solid rgba(239, 68, 68, 0.25)",
              }}>
                <span style={{
                  width: 6.5,
                  height: 6.5,
                  borderRadius: "50%",
                  background: item.isActive ? "#10B981" : "#EF4444",
                  boxShadow: item.isActive ? "0 0 0 2.5px rgba(16, 185, 129, 0.2)" : "0 0 0 2.5px rgba(239, 68, 68, 0.2)",
                }} />
                {item.isActive ? "Live" : "Hidden"}
              </span>
              {item.displaySection && (
                <span className="rv-section-badge">{item.displaySection}</span>
              )}
            </div>
            <p className="rv-modal-sub" style={{ marginTop: 2 }}>
              {product?.productCode ? `${product.productCode} · ` : ""}{product?.productName || "Fine Jewellery Piece"}
            </p>
          </div>
          <button className="rv-modal-close" onClick={onClose}><CloseIcon /></button>
        </div>

        {/* Body */}
        <div className="rv-modal-body" style={{ overflowY: "auto", flex: 1, padding: "20px 24px" }}>
          <div className="rv-cat-detail-grid">

            {/* Left: Photos Gallery */}
            <div>
              <div style={{
                width: "100%",
                height: 310,
                background: "#FAF7F2",
                borderRadius: 12,
                border: "1px solid rgba(185,167,146,0.3)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                position: "relative",
                overflow: "hidden",
                boxShadow: "0 4px 16px rgba(0,0,0,0.04)"
              }}>
                {loadingPhotos ? (
                  <div className="rv-loader" />
                ) : activeSrc ? (
                  <img
                    src={activeSrc}
                    alt={item.displayName}
                    style={{ width: "100%", height: "100%", objectFit: "contain", padding: 8 }}
                  />
                ) : (
                  <div style={{ textAlign: "center", color: "var(--taupe)", padding: 20 }}>
                    <div style={{ marginBottom: 8, display: "flex", justifyContent: "center" }}><DiamondIcon /></div>
                    <p style={{ fontSize: 13, fontWeight: 600, margin: 0 }}>No Photos Uploaded Yet</p>
                    <p style={{ fontSize: 11.5, margin: "4px 0 0" }}>Add images using the photo manager</p>
                  </div>
                )}

                {activePhoto?.isPrimary && (
                  <span style={{
                    position: "absolute",
                    top: 10,
                    left: 10,
                    background: "rgba(185,167,146,0.92)",
                    color: "#fff",
                    fontSize: 10,
                    fontWeight: 700,
                    padding: "3px 8px",
                    borderRadius: 20,
                    letterSpacing: "0.04em",
                    backdropFilter: "blur(4px)"
                  }}>
                    PRIMARY COVER
                  </span>
                )}
              </div>

              {/* Thumbnails strip */}
              {photos.length > 1 && (
                <div style={{ display: "flex", gap: 8, marginTop: 10, overflowX: "auto", paddingBottom: 4 }}>
                  {photos.map((ph, idx) => {
                    const src = getPhotoSrc(ph);
                    const isSelected = (activePhotoIdx === idx);
                    return (
                      <button
                        key={ph.photoID || idx}
                        type="button"
                        onClick={() => setActivePhotoIdx(idx)}
                        style={{
                          width: 54,
                          height: 54,
                          borderRadius: 8,
                          overflow: "hidden",
                          border: isSelected ? "2px solid var(--taupe-dark)" : "1px solid rgba(185,167,146,0.3)",
                          background: "#FAF7F2",
                          padding: 0,
                          cursor: "pointer",
                          flexShrink: 0,
                          outline: "none",
                          boxShadow: isSelected ? "0 2px 8px rgba(0,0,0,0.12)" : "none",
                          opacity: isSelected ? 1 : 0.7,
                          transition: "all 0.15s ease"
                        }}
                      >
                        {src ? (
                          <img src={src} alt="thumb" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                        ) : (
                          <ImageIcon />
                        )}
                      </button>
                    );
                  })}
                </div>
              )}

              <button
                type="button"
                className="rv-btn rv-btn--ghost"
                onClick={() => onManagePhotos(item)}
                style={{ width: "100%", marginTop: 12, justifyContent: "center", border: "1px dashed rgba(185,167,146,0.5)" }}
              >
                <ImageIcon /> Manage Gallery ({photos.length} photos)
              </button>
            </div>

            {/* Right: Specifications & Internal Comments */}
            <div>
              {/* Description Label / Tagline */}
              {item.descriptionLbl && (
                <div style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: "var(--taupe-dark)",
                  marginBottom: 14,
                  padding: "6px 12px",
                  background: "rgba(185,167,146,0.12)",
                  borderRadius: 6,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6
                }}>
                  <TagIcon /> {item.descriptionLbl}
                </div>
              )}

              {/* INTERNAL COMMENTS (Dedicated Admin Section) */}
              <div style={{
                background: item.comments ? "rgba(197, 160, 89, 0.08)" : "#FAF8F5",
                border: "1px solid rgba(197, 160, 89, 0.28)",
                borderLeft: "4px solid #C5A059",
                borderRadius: 8,
                padding: "12px 16px",
                marginBottom: 16,
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, fontWeight: 700, color: "#8B6508", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 }}>
                  <CommentIcon /> <span>Internal Comments (Staff Only)</span>
                </div>
                <p style={{
                  margin: 0,
                  fontSize: 13,
                  color: item.comments ? "#333" : "var(--taupe)",
                  fontStyle: item.comments ? "normal" : "italic",
                  lineHeight: 1.5,
                  whiteSpace: "pre-wrap"
                }}>
                  {item.comments || "No internal comments entered for this piece."}
                </p>
              </div>

              {/* Rich Text Catalogue Description */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "var(--taupe)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>
                  Catalogue Description (Public)
                </div>
                <div style={{
                  background: "#fff",
                  border: "1px solid rgba(185,167,146,0.25)",
                  borderRadius: 8,
                  padding: "12px 16px",
                  maxHeight: 180,
                  overflowY: "auto",
                }}>
                  {item.description ? (
                    <div
                      className="ql-editor"
                      style={{ padding: 0, minHeight: "auto", fontSize: 13, color: "var(--dark)", lineHeight: 1.6 }}
                      dangerouslySetInnerHTML={{
                        __html: /<[a-z][\s\S]*>/i.test(item.description)
                          ? item.description
                          : item.description.replace(/\n/g, "<br/>"),
                      }}
                    />
                  ) : (
                    <em style={{ color: "var(--taupe)", fontSize: 12.5 }}>No detailed description provided.</em>
                  )}
                </div>
              </div>

              {/* Specifications Table */}
              <div style={{
                background: "#FAF7F2",
                borderRadius: 8,
                border: "1px solid rgba(185,167,146,0.2)",
                overflow: "hidden"
              }}>
                <div style={{ padding: "8px 12px", background: "rgba(185,167,146,0.15)", fontSize: 11, fontWeight: 700, color: "var(--taupe)", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  Product Specifications
                </div>
                <table style={{ width: "100%", fontSize: 12.5, borderCollapse: "collapse" }}>
                  <tbody>
                    <tr style={{ borderBottom: "1px solid rgba(185,167,146,0.15)" }}>
                      <td style={{ padding: "8px 12px", color: "var(--taupe)", width: "40%" }}>Linked Product</td>
                      <td style={{ padding: "8px 12px", fontWeight: 600, color: "var(--dark)" }}>{product?.productName || item.displayName || `Product #${item.productID}`}</td>
                    </tr>
                    <tr style={{ borderBottom: "1px solid rgba(185,167,146,0.15)" }}>
                      <td style={{ padding: "8px 12px", color: "var(--taupe)" }}>Product Code</td>
                      <td style={{ padding: "8px 12px", fontFamily: "monospace", fontWeight: 600 }}>
                        {product?.productCode ? (
                          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                            <span>{product.productCode}</span>
                            <button
                              type="button"
                              onClick={() => {
                                navigator.clipboard?.writeText(product.productCode);
                                showToast(`Copied code: ${product.productCode}`);
                              }}
                              title="Copy product code"
                              style={{ background: "none", border: "none", cursor: "pointer", color: "var(--taupe)", padding: 2, display: "inline-flex" }}
                            >
                              <CopyIcon />
                            </button>
                          </span>
                        ) : "—"}
                      </td>
                    </tr>
                    <tr style={{ borderBottom: "1px solid rgba(185,167,146,0.15)" }}>
                      <td style={{ padding: "8px 12px", color: "var(--taupe)" }}>Purity / Standard</td>
                      <td style={{ padding: "8px 12px", fontWeight: 600 }}>{product?.purityStandard || "—"}</td>
                    </tr>
                    <tr style={{ borderBottom: "1px solid rgba(185,167,146,0.15)" }}>
                      <td style={{ padding: "8px 12px", color: "var(--taupe)" }}>Pricing Type</td>
                      <td style={{ padding: "8px 12px" }}>{product?.pricingType || "—"}</td>
                    </tr>
                    <tr>
                      <td style={{ padding: "8px 12px", color: "var(--taupe)" }}>Section / Category</td>
                      <td style={{ padding: "8px 12px", fontWeight: 600 }}>{item.displaySection || "—"}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="rv-modal-footer rv-cat-detail-footer">
          <div className="rv-cat-detail-footer-group rv-cat-detail-footer-group--showcase">
            <a
              href={`/catalogue?item=${item.catalogueItemID}`}
              target="_blank"
              rel="noopener noreferrer"
              className="rv-btn rv-btn--ghost"
              style={{ textDecoration: "none" }}
              title="Open customer showcase in new tab"
            >
              <ExternalLinkIcon /> <span>View Showcase</span>
            </a>
            <button
              type="button"
              className="rv-btn rv-btn--ghost"
              onClick={() => {
                const url = `${window.location.origin}/catalogue?item=${item.catalogueItemID}`;
                navigator.clipboard?.writeText(url);
                showToast("Customer showcase link copied!");
              }}
              title="Copy shareable link"
            >
              <CopyIcon /> <span>Copy Link</span>
            </button>
          </div>
          <div className="rv-cat-detail-footer-group rv-cat-detail-footer-group--admin">
            <button
              type="button"
              className="rv-btn rv-btn--primary"
              onClick={() => {
                onClose();
                onEdit(item);
              }}
            >
              <EditIcon /> <span>Edit Item</span>
            </button>
            <button type="button" className="rv-btn rv-btn--ghost" onClick={onClose}>
              <span>Close</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════
   PHOTO PANEL DRAWER (per row)
══════════════════════════════════════ */
function PhotoPanel({ item, onClose, showToast }) {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadPhotos = async () => {
    setLoading(true);
    try {
      const data = await cataloguePhotoApi.getByItemId(item.catalogueItemID);
      setPhotos(Array.isArray(data) ? data : data?.data ?? []);
    } catch {
      setPhotos([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadPhotos(); }, [item.catalogueItemID]);

  return (
    <div className="rv-dialog-backdrop" onClick={onClose} style={{ zIndex: 600 }}>
      <div className="rv-photo-panel" onClick={e => e.stopPropagation()}>
        <div className="rv-photo-panel-header">
          <div className="rv-photo-panel-title-wrap">
            <div className="rv-history-icon"><ImageIcon /></div>
            <div>
              <h3 className="rv-history-title">Photos</h3>
              <p className="rv-history-sub">{item.displayName}</p>
            </div>
          </div>
          <button className="rv-modal-close" onClick={onClose}><CloseIcon /></button>
        </div>
        <div className="rv-photo-panel-body">
          {loading ? (
            <div className="rv-cat-empty">
              <div className="rv-loader" /><p>Loading photos…</p>
            </div>
          ) : (
            <PhotoManager
              itemID={item.catalogueItemID}
              photos={photos}
              onPhotosChange={loadPhotos}
              showToast={showToast}
            />
          )}
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   SECTION EDITOR DRAWER
══════════════════════════════════════════════════════════ */
function SectionEditorDrawer({ section, defaultTab, catalogueItems, products = [], onSave, onClose, showToast }) {
  const isEdit = !!section?.sectionID;
  const [drawerTab, setDrawerTab] = useState(defaultTab || "general");
  const [form, setForm] = useState({
    title: section?.title ?? section?.publicTitle ?? "",
    publicTitle: section?.title ?? section?.publicTitle ?? "",
    internalName: section?.internalName ?? section?.slug ?? "",
    subtitle: section?.subtitle ?? "",
    slug: section?.slug ?? "",
    sectionType: section?.sectionType ?? "Collection",
    layoutTemplate: section?.layoutTemplate ?? "Grid",
    layoutType: section?.layoutType ?? "grid",
    placement: section?.placement ?? "HOMEPAGE",
    isPublished: section?.isPublished !== undefined ? Boolean(section.isPublished) : true,
  });
  const [assignedProducts, setAssignedProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [savingDrawer, setSavingDrawer] = useState(false);
  const [productSearch, setProductSearch] = useState("");
  const [assigningId, setAssigningId] = useState("");
  const [badgeInput, setBadgeInput] = useState("");
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const dragSrcIdx = useRef(null);
  const [copiedCode, setCopiedCode] = useState(null);

  /* Load assigned products when editing */
  useEffect(() => {
    if (!isEdit || !section?.sectionID) return;
    setLoadingProducts(true);
    catalogueSectionApi.getProducts(section.sectionID)
      .then((data) => setAssignedProducts(Array.isArray(data) ? data : []))
      .catch(() => showToast("Failed to load products.", "error"))
      .finally(() => setLoadingProducts(false));
  }, [section?.sectionID, isEdit, showToast]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  /* Auto-slug from title */
  const handleTitleChange = (val) => {
    setField("title", val);
    setField("publicTitle", val);
    const generated = val.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
    if (!isEdit || !form.slug) {
      setField("slug", generated);
    }
  };

  /* Save general info + visibility */
  const handleSaveGeneral = async () => {
    const titleVal = (form.title || form.publicTitle || "").trim();
    if (!titleVal) { showToast("Title is required.", "error"); return; }
    if (titleVal.length > 200) { showToast("Title cannot exceed 200 characters.", "error"); return; }
    if ((form.subtitle || "").trim().length > 300) { showToast("Subtitle cannot exceed 300 characters.", "error"); return; }
    
    // Auto-generate slug behind the scenes with unique suffix for new sections
    const baseSlug = (titleVal.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "section").trim();
    const derivedSlug = isEdit
      ? (form.slug || baseSlug)
      : `${baseSlug}-${Date.now().toString().slice(-4)}`;

    setSavingDrawer(true);
    try {
      const payload = {
        title: titleVal,
        slug: derivedSlug,
        subtitle: form.subtitle || null,
        sectionType: form.sectionType || "Collection",
        layoutTemplate: form.layoutTemplate || "Grid",
        layoutType: form.layoutType || "grid",
        isPublished: form.isPublished,
        isArchived: false,
      };
      const saved = isEdit
        ? await catalogueSectionApi.update(section.sectionID, payload)
        : await catalogueSectionApi.create(payload);
      showToast(isEdit ? "Section updated." : "Section created.");
      onSave(saved);
    } catch (err) {
      showToast(err.message || "Save failed.", "error");
    } finally {
      setSavingDrawer(false);
    }
  };

  /* Assign product */
  const handleAssign = async () => {
    if (!assigningId) return;
    if (!section?.sectionID) { showToast("Save the section first.", "error"); return; }
    if ((badgeInput || "").trim().length > 50) {
      showToast("Badge text cannot exceed 50 characters.", "error");
      return;
    }
    const alreadyIn = assignedProducts.some(m => Number(m.catalogueItemID) === Number(assigningId));
    if (alreadyIn) {
      showToast("This catalogue item is already in the section.", "error");
      return;
    }
    try {
      const mapping = await catalogueSectionApi.assignProduct(
        section.sectionID, Number(assigningId), badgeInput || null
      );
      setAssignedProducts((p) => [...p, mapping]);
      setAssigningId(""); setBadgeInput("");
      showToast("Product assigned.");
    } catch (err) {
      showToast(err.message || "Assign failed.", "error");
    }
  };

  /* Remove product */
  const handleRemove = async (catalogueItemID) => {
    if (!section?.sectionID) return;
    try {
      await catalogueSectionApi.removeProduct(section.sectionID, catalogueItemID);
      setAssignedProducts((p) => p.filter((m) => m.catalogueItemID !== catalogueItemID));
      showToast("Product removed.");
    } catch (err) {
      showToast(err.message || "Remove failed.", "error");
    }
  };

  /* Drag-to-reorder products */
  const handleDragStart = (idx) => { dragSrcIdx.current = idx; };
  const handleDragOver = (e, idx) => { e.preventDefault(); setDragOverIdx(idx); };
  const handleDragEnd = () => { dragSrcIdx.current = null; setDragOverIdx(null); };
  const handleDrop = async (e, dropIdx) => {
    e.preventDefault();
    const srcIdx = dragSrcIdx.current;
    if (srcIdx === null || srcIdx === dropIdx) { setDragOverIdx(null); return; }
    const reordered = [...assignedProducts];
    const [moved] = reordered.splice(srcIdx, 1);
    reordered.splice(dropIdx, 0, moved);
    const updated = reordered.map((item, idx) => ({ ...item, displayOrder: idx + 1 }));
    setAssignedProducts(updated);
    setDragOverIdx(null);
    dragSrcIdx.current = null;
    if (section?.sectionID) {
      await catalogueSectionApi.reorderProducts(
        section.sectionID,
        updated
      ).catch(() => { });
    }
  };

  const getItemInfo = (id) => {
    const ci = catalogueItems.find((i) => i.catalogueItemID === Number(id));
    const prod = ci ? products.find((p) => p.productID === ci.productID) : null;
    return {
      displayName: ci?.displayName || `Item #${id}`,
      productCode: prod?.productCode || "",
      displaySection: ci?.displaySection || "",
    };
  };

  const getItemName = (id) => getItemInfo(id).displayName;

  const handleCopyCode = (code, e) => {
    if (e) e.stopPropagation();
    if (!code) return;
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(code);
      setCopiedCode(code);
      showToast(`Copied code: ${code}`);
      setTimeout(() => setCopiedCode(null), 2000);
    }
  };

  const availableToAssign = catalogueItems.filter((ci) => {
    const alreadyAssigned = assignedProducts.some(
      (m) => m.catalogueItemID === ci.catalogueItemID
    );
    const prod = products.find((p) => p.productID === ci.productID);
    const prodCode = prod?.productCode || "";
    const q = productSearch.toLowerCase().trim();
    const matchSearch =
      !q ||
      ci.displayName?.toLowerCase().includes(q) ||
      prodCode.toLowerCase().includes(q);
    return !alreadyAssigned && matchSearch;
  });

  return (
    <>
      {/* Backdrop */}
      <div
        style={{
          position: "fixed", inset: 0,
          background: "rgba(51,47,47,0.35)",
          backdropFilter: "blur(2px)",
          zIndex: 700,
        }}
        onClick={onClose}
      />
      {/* Drawer */}
      <div
        style={{
          position: "fixed", top: 0, right: 0, bottom: 0,
          width: 620, maxWidth: "95vw",
          background: "#fff",
          boxShadow: "-8px 0 48px rgba(0,0,0,0.18)",
          zIndex: 701,
          display: "flex", flexDirection: "column",
          borderLeft: "1px solid rgba(185,167,146,0.2)",
          animation: "rv-drawer-slide-in 0.28s cubic-bezier(0.16,1,0.3,1)",
        }}
      >
        {/* Drawer Header */}
        <div style={{
          padding: "20px 24px",
          borderBottom: "1px solid rgba(185,167,146,0.2)",
          display: "flex", alignItems: "center", gap: 14,
          background: "#faf4f0",
        }}>
          <div style={{
            width: 40, height: 40, borderRadius: 10,
            background: "rgba(185,167,146,0.2)",
            display: "flex", alignItems: "center", justifyContent: "center",
            color: "#B9A792",
          }}>
            <LayersIcon />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "#332F2F" }}>
              {isEdit ? "Edit Section" : "Create Section"}
            </h3>
            <p style={{ margin: 0, fontSize: 12, color: "#736B66" }}>
              {isEdit ? (section.title || section.publicTitle || "Section Settings") : "Configure a new homepage section"}
            </p>
          </div>
          <button className="rv-modal-close" onClick={onClose}><CloseIcon /></button>
        </div>

        {/* Drawer Tabs */}
        <div style={{
          display: "flex", gap: 2, padding: "12px 24px 0",
          borderBottom: "1px solid rgba(185,167,146,0.15)",
          background: "#faf4f0",
        }}>
          {[
            { key: "general", label: "General Info" },
            { key: "products", label: `Assign Products${assignedProducts.length ? ` (${assignedProducts.length})` : ""}` },
            { key: "visibility", label: "Visibility" },
          ].map((t) => (
            <button
              key={t.key}
              onClick={() => setDrawerTab(t.key)}
              style={{
                padding: "8px 14px",
                fontSize: 12.5, fontWeight: 600,
                border: "none", borderBottom: drawerTab === t.key ? "2px solid #B9A792" : "2px solid transparent",
                background: "transparent",
                color: drawerTab === t.key ? "#332F2F" : "#736B66",
                cursor: "pointer", marginBottom: -1,
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Drawer Body */}
        <div style={{ flex: 1, overflowY: "auto", padding: "24px" }}>

          {/* ── Tab: General Info ── */}
          {drawerTab === "general" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
              <div className="rv-field">
                <label className="rv-label">Section Title (Public) <span className="rv-required">*</span></label>
                <input
                  className="rv-input"
                  placeholder="e.g. Bridal Edit — Curated Collection"
                  maxLength={200}
                  value={form.publicTitle}
                  onChange={(e) => handleTitleChange(e.target.value)}
                />
              </div>
              {/* 
              <div className="rv-field">
                <label className="rv-label">Subtitle / Tagline</label>
                <input
                  className="rv-input"
                  placeholder="Short descriptor shown below the section heading"
                  value={form.subtitle}
                  onChange={(e) => setField("subtitle", e.target.value)}
                />
              </div>
              <div className="rv-field-row-2col">
                <div className="rv-field">
                  <label className="rv-label">Internal Reference</label>
                  <input
                    className="rv-input"
                    placeholder="e.g. bridal-collection"
                    value={form.internalName}
                    onChange={(e) => setField("internalName", e.target.value)}
                  />
                  <p className="rv-field-hint" style={{ margin: "4px 0 0", fontSize: 11.5 }}>Only visible to store staff — not shown to customers</p>
                </div>
                <div className="rv-field">
                  <label className="rv-label">Web Page Address <span className="rv-required">*</span></label>
                  <input
                    className="rv-input"
                    placeholder="e.g. bridal-collection"
                    value={form.slug}
                    onChange={(e) => setField("slug", e.target.value)}
                  />
                  <p className="rv-field-hint" style={{ margin: "4px 0 0", fontSize: 11.5 }}>Auto-generated from title. You can customise it but avoid spaces.</p>
                </div>
              </div>
              */}
              <div className="rv-field">
                <label className="rv-label">Layout Type</label>
                <select
                  className="rv-input"
                  value={form.layoutType || "grid"}
                  onChange={(e) => setField("layoutType", e.target.value)}
                >
                  <option value="grid">Grid</option>
                  <option value="carousel">Carousel</option>
                  <option value="horizontal">Horizontal Scroll</option>
                </select>
              </div>
            </div>
          )}

          {/* ── Tab: Assign Products ── */}
          {drawerTab === "products" && (
            <div>
              {!isEdit && (
                <div style={{
                  background: "rgba(197,160,89,0.08)",
                  border: "1px solid rgba(197,160,89,0.25)",
                  borderRadius: 8, padding: "12px 16px",
                  fontSize: 12.5, color: "#8B6508", marginBottom: 20,
                }}>
                  ⚡ Save the section first (General Info tab) before assigning products.
                </div>
              )}

              {/* Search & Assign */}
              {isEdit && (
                <div style={{ marginBottom: 20 }}>
                  <p style={{ fontSize: 12, fontWeight: 700, color: "#736B66", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>Add Product to Section</p>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <input
                      className="rv-input"
                      placeholder="Search catalogue items…"
                      value={productSearch}
                      onChange={(e) => setProductSearch(e.target.value)}
                      style={{ flex: "1 1 180px" }}
                    />
                    <input
                      className="rv-input"
                      placeholder="Badge (optional)"
                      value={badgeInput}
                      onChange={(e) => setBadgeInput(e.target.value)}
                      style={{ flex: "0 0 140px" }}
                    />
                  </div>
                  <div style={{ maxHeight: 180, overflowY: "auto", marginTop: 8, border: "1px solid rgba(185,167,146,0.2)", borderRadius: 8 }}>
                    {availableToAssign.length === 0 ? (
                      <p style={{ padding: "12px 16px", color: "#736B66", fontSize: 12.5, margin: 0 }}>No matching items.</p>
                    ) : availableToAssign.map((ci) => {
                      const prod = products.find((p) => p.productID === ci.productID);
                      const prodCode = prod?.productCode;
                      return (
                        <div
                          key={ci.catalogueItemID}
                          onClick={() => setAssigningId(String(ci.catalogueItemID))}
                          style={{
                            padding: "9px 14px",
                            cursor: "pointer",
                            fontSize: 13,
                            color: assigningId === String(ci.catalogueItemID) ? "#332F2F" : "#736B66",
                            background: assigningId === String(ci.catalogueItemID) ? "rgba(185,167,146,0.12)" : "transparent",
                            borderBottom: "1px solid rgba(185,167,146,0.1)",
                            fontWeight: assigningId === String(ci.catalogueItemID) ? 600 : 400,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 10,
                          }}
                        >
                          <div>
                            <span>{ci.displayName}</span>
                            {ci.displaySection && <span style={{ fontSize: 11, color: "#B9A792", marginLeft: 8 }}>{ci.displaySection}</span>}
                          </div>
                          {prodCode && (
                            <div style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                              <span style={{
                                fontSize: 11,
                                fontFamily: "monospace",
                                fontWeight: 700,
                                color: "#736B66",
                                background: "rgba(185,167,146,0.16)",
                                padding: "2px 6px",
                                borderRadius: 4,
                              }}>
                                #{prodCode}
                              </span>
                              <button
                                type="button"
                                onClick={(e) => handleCopyCode(prodCode, e)}
                                title="Copy product code"
                                style={{
                                  border: "none",
                                  background: "transparent",
                                  cursor: "pointer",
                                  padding: "2px 4px",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  color: copiedCode === prodCode ? "#2E7D32" : "#8F7B67",
                                  fontSize: 12,
                                }}
                              >
                                {copiedCode === prodCode ? "✓" : <CopyIcon />}
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  {assigningId && (
                    <button
                      className="rv-btn rv-btn--primary"
                      onClick={handleAssign}
                      style={{ marginTop: 10 }}
                    >
                      <PlusIcon /> Assign: {getItemName(assigningId)}
                    </button>
                  )}
                </div>
              )}

              {/* Assigned Products List */}
              <p style={{ fontSize: 12, fontWeight: 700, color: "#736B66", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>Assigned ({assignedProducts.length})</p>
              {loadingProducts ? (
                <div className="rv-cat-pg-empty"><div className="rv-loader" /></div>
              ) : assignedProducts.length === 0 ? (
                <p style={{ fontSize: 13, color: "#B9A792", fontStyle: "italic" }}>No products assigned yet.</p>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {assignedProducts
                    .slice()
                    .sort((a, b) => a.displayOrder - b.displayOrder)
                    .map((mapping, idx) => {
                      const info = getItemInfo(mapping.catalogueItemID);
                      return (
                        <div
                          key={mapping.catalogueItemID}
                          draggable
                          onDragStart={() => handleDragStart(idx)}
                          onDragOver={(e) => handleDragOver(e, idx)}
                          onDrop={(e) => handleDrop(e, idx)}
                          onDragLeave={() => setDragOverIdx(null)}
                          onDragEnd={handleDragEnd}
                          style={{
                            display: "flex", alignItems: "center", gap: 10,
                            padding: "10px 14px",
                            borderRadius: 8,
                            border: dragOverIdx === idx ? "2px solid #B9A792" : "1px solid rgba(185,167,146,0.2)",
                            background: dragOverIdx === idx ? "rgba(185,167,146,0.08)" : "#FAFAFA",
                            cursor: "grab",
                            transition: "all 0.15s",
                          }}
                        >
                          <GripIcon />
                          <span style={{ fontSize: 11, fontWeight: 700, color: "#B9A792", minWidth: 20 }}>#{idx + 1}</span>
                          <div style={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                            <span style={{ fontSize: 13, fontWeight: 600, color: "#332F2F" }}>{info.displayName}</span>
                            {info.productCode && (
                              <div style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                                <span style={{
                                  fontSize: 11,
                                  fontWeight: 700,
                                  fontFamily: "monospace",
                                  padding: "2px 6px",
                                  borderRadius: 4,
                                  background: "rgba(185,167,146,0.18)",
                                  color: "#594F48",
                                }}>
                                  #{info.productCode}
                                </span>
                                <button
                                  type="button"
                                  onClick={(e) => handleCopyCode(info.productCode, e)}
                                  title="Copy product code"
                                  style={{
                                    border: "none",
                                    background: "transparent",
                                    cursor: "pointer",
                                    padding: "2px 4px",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    color: copiedCode === info.productCode ? "#2E7D32" : "#8F7B67",
                                    fontSize: 12,
                                  }}
                                >
                                  {copiedCode === info.productCode ? "✓" : <CopyIcon />}
                                </button>
                              </div>
                            )}
                          </div>
                          {mapping.badgeText && (
                            <span style={{
                              fontSize: 10, fontWeight: 700, padding: "2px 7px",
                              borderRadius: 20, background: "rgba(197,160,89,0.15)", color: "#8B6508",
                            }}>
                              {mapping.badgeText}
                            </span>
                          )}
                          {isEdit && (
                            <button
                              className="rv-action-btn rv-action-btn--del"
                              onClick={() => handleRemove(mapping.catalogueItemID)}
                              title="Remove"
                            >
                              <TrashIcon />
                            </button>
                          )}
                        </div>
                      );
                    })}
                </div>
              )}
            </div>
          )}

          {/* ── Tab: Visibility ── */}
          {drawerTab === "visibility" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
              <div className="rv-field rv-field--row">
                <div>
                  <label className="rv-label">Published</label>
                  <p className="rv-field-hint">Visible on the public website</p>
                </div>
                <button
                  type="button"
                  className={`rv-toggle ${form.isPublished ? "rv-toggle--on" : ""}`}
                  onClick={() => setField("isPublished", !form.isPublished)}
                >
                  <span className="rv-toggle-knob" />
                </button>
              </div>
              <div style={{
                background: form.isPublished ? "rgba(27,109,36,0.06)" : "rgba(180,83,9,0.06)",
                border: `1px solid ${form.isPublished ? "rgba(27,109,36,0.2)" : "rgba(180,83,9,0.2)"}`,
                borderRadius: 8, padding: "12px 16px", fontSize: 12.5,
                color: form.isPublished ? "#1b6d24" : "#b45309",
              }}>
                {form.isPublished
                  ? "✅ This section will appear on the public website."
                  : "⏸ This section is a Draft and hidden from the public website."}
              </div>
            </div>
          )}
        </div>

        {/* Drawer Footer */}
        <div style={{
          padding: "16px 24px",
          borderTop: "1px solid rgba(185,167,146,0.2)",
          display: "flex", gap: 10, justifyContent: "flex-end",
          background: "#faf4f0",
        }}>
          <button className="rv-btn rv-btn--ghost" onClick={onClose}>Cancel</button>
          {drawerTab !== "products" && (
            <button
              className="rv-btn rv-btn--primary"
              onClick={handleSaveGeneral}
              disabled={savingDrawer}
            >
              {savingDrawer ? <span className="rv-spinner" /> : null}
              {isEdit ? "Save Changes" : "Create Section"}
            </button>
          )}
          {drawerTab === "products" && isEdit && (
            <button
              className="rv-btn rv-btn--primary"
              onClick={() => { showToast("Product order saved."); onSave(section); }}
            >
              Save Order
            </button>
          )}
        </div>
      </div>
    </>
  );
}


/* ══════════════════════════════════════
   MANAGE SECTIONS TAB
══════════════════════════════════════ */
function ManageSectionsTab({ catalogueItems, products = [], showToast }) {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drawer, setDrawer] = useState(null); // null | { section } | { section: {} }
  const [archiving, setArchiving] = useState(null);
  const [search, setSearch] = useState("");
  const [savingOrder, setSavingOrder] = useState(false);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const dragSrcIdx = useRef(null);

  const loadSections = useCallback(async () => {
    setLoading(true);
    try {
      const [data, layoutIds] = await Promise.all([
        catalogueSectionApi.getAll(),
        pageLayoutApi.getDraft("homepage").catch(() => []),
      ]);
      const secList = Array.isArray(data) ? data : [];
      const ids = Array.isArray(layoutIds) ? layoutIds : [];

      const sorted = [...secList].sort((a, b) => {
        const idxA = ids.indexOf(a.sectionID);
        const idxB = ids.indexOf(b.sectionID);
        if (idxA !== -1 && idxB !== -1) return idxA - idxB;
        if (idxA !== -1) return -1;
        if (idxB !== -1) return 1;
        return (a.displayOrder || 0) - (b.displayOrder || 0);
      });
      const withOrder = sorted.map((s, idx) => ({ ...s, displayOrder: idx + 1 }));
      setSections(withOrder);
    } catch (err) {
      showToast(err.message || "Failed to load sections.", "error");
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => { loadSections(); }, [loadSections]);

  const persistOrder = async (orderedList) => {
    setSavingOrder(true);
    try {
      const ids = orderedList.map((s) => s.sectionID);
      await pageLayoutApi.saveDraft("homepage", ids);
      await pageLayoutApi.publish("homepage");
      showToast("Homepage section order updated.");
    } catch (err) {
      console.warn("pageLayout order save fallback:", err);
      showToast("Order saved locally (" + (err.message || "publish warning") + ")", "warning");
    } finally {
      setSavingOrder(false);
    }
  };

  const moveSection = (srcIdx, destIdx) => {
    if (search.trim()) {
      showToast("Please clear the search filter before arranging sections.", "warning");
      return;
    }
    if (destIdx < 0 || destIdx >= sections.length || srcIdx === destIdx) return;
    const next = [...sections];
    const [moved] = next.splice(srcIdx, 1);
    next.splice(destIdx, 0, moved);
    const reindexed = next.map((s, idx) => ({ ...s, displayOrder: idx + 1 }));
    setSections(reindexed);
    persistOrder(reindexed);
  };

  const handleDragStart = (idx) => {
    if (search.trim()) return;
    dragSrcIdx.current = idx;
  };

  const handleDragOver = (e, idx) => {
    e.preventDefault();
    if (search.trim()) return;
    setDragOverIdx(idx);
  };

  const handleDragEnd = () => {
    dragSrcIdx.current = null;
    setDragOverIdx(null);
  };

  const handleDrop = (e, dropIdx) => {
    e.preventDefault();
    if (search.trim()) return;
    const src = dragSrcIdx.current;
    if (src === null || src === dropIdx) {
      setDragOverIdx(null);
      return;
    }
    moveSection(src, dropIdx);
    setDragOverIdx(null);
    dragSrcIdx.current = null;
  };

  const handleSaved = (saved) => {
    setSections((prev) => {
      const idx = prev.findIndex((s) => s.sectionID === saved.sectionID);
      let updated;
      if (idx === -1) {
        updated = [...prev, { ...saved, displayOrder: prev.length + 1 }];
      } else {
        updated = prev.map((s, i) => (i === idx ? { ...s, ...saved, displayOrder: s.displayOrder } : s));
      }
      persistOrder(updated);
      return updated;
    });
    setDrawer(null);
  };

  const handleArchive = async (sectionID, name) => {
    if (!window.confirm(`Archive "${name}"? This removes it from the homepage.`)) return;
    setArchiving(sectionID);
    try {
      await catalogueSectionApi.archive(sectionID);
      const remaining = sections
        .filter((s) => s.sectionID !== sectionID)
        .map((s, idx) => ({ ...s, displayOrder: idx + 1 }));
      setSections(remaining);
      persistOrder(remaining);
      showToast("Section archived.");
    } catch (err) {
      showToast(err.message || "Archive failed.", "error");
    } finally {
      setArchiving(null);
    }
  };

  const sectionTypeLabel = (t) => ({
    HERO_BANNER: "Hero Banner",
    CURATED_GRID: "Curated Grid",
    CAROUSEL: "Carousel",
  }[t] ?? t);

  const filteredSections = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return sections;
    return sections.filter((s) =>
      (s.publicTitle || "").toLowerCase().includes(q) ||
      (s.subtitle || "").toLowerCase().includes(q) ||
      (s.sectionType || "").toLowerCase().includes(q) ||
      (s.slug || "").toLowerCase().includes(q)
    );
  }, [sections, search]);

  return (
    <div>
      {/* Sub-header with Search */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, flexWrap: "wrap", gap: 14 }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: "#332F2F" }}>Homepage Sections</h3>
            {savingOrder && (
              <span style={{ fontSize: 11, color: "#8B6508", display: "inline-flex", alignItems: "center", gap: 4 }}>
                <span className="rv-pm-mini-spin" /> Saving sequence…
              </span>
            )}
          </div>
          <p style={{ margin: "2px 0 0", fontSize: 12, color: "#736B66" }}>
            {search.trim() ? `${filteredSections.length} of ${sections.length} sections` : `${sections.length} section${sections.length !== 1 ? "s" : ""} configured`}
            {" · "}
            <span style={{ color: "#8F7B67" }}>Drag cards or use ▲ / ▼ to arrange sequence for public catalogue</span>
          </p>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <div className="rv-cat-pg-search" style={{ minWidth: 240, maxWidth: 360 }}>
            <SearchIcon />
            <input
              type="text"
              placeholder="Search sections by title, type, slug…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <button
                type="button"
                className="rv-cat-pg-search-clear"
                onClick={() => setSearch("")}
                title="Clear search"
              >
                <CloseIcon />
              </button>
            )}
          </div>

          <button
            className="rv-btn rv-btn--primary"
            onClick={() => setDrawer({ section: {} })}
          >
            <PlusIcon /> Create Section
          </button>
        </div>
      </div>

      {/* Section Cards */}
      {loading ? (
        <div className="rv-cat-pg-empty"><div className="rv-loader" /><p>Loading sections…</p></div>
      ) : sections.length === 0 ? (
        <div className="rv-cat-pg-empty">
          <div className="rv-empty-icon"><LayersIcon /></div>
          <p className="rv-empty-title">No sections yet</p>
          <p className="rv-empty-sub">Create your first homepage section to get started.</p>
          <button className="rv-btn rv-btn--primary rv-mt-12" onClick={() => setDrawer({ section: {} })}>
            <PlusIcon /> Create Section
          </button>
        </div>
      ) : filteredSections.length === 0 ? (
        <div className="rv-cat-pg-empty">
          <div className="rv-empty-icon"><SearchIcon /></div>
          <p className="rv-empty-title">No sections match "${search}"</p>
          <p className="rv-empty-sub">Try searching for a different title, subtitle, or section type.</p>
          <button className="rv-btn rv-btn--ghost rv-mt-12" onClick={() => setSearch("")}>
            Clear Search
          </button>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {filteredSections.map((sec, idx) => {
            const isDraggingOver = dragOverIdx === idx;
            const canReorder = !search.trim();
            const isFirst = idx === 0;
            const isLast = idx === filteredSections.length - 1;

            return (
              <div
                key={sec.sectionID}
                draggable={canReorder}
                onDragStart={() => handleDragStart(idx)}
                onDragOver={(e) => handleDragOver(e, idx)}
                onDrop={(e) => handleDrop(e, idx)}
                onDragLeave={() => setDragOverIdx(null)}
                onDragEnd={handleDragEnd}
                style={{
                  background: isDraggingOver ? "rgba(185, 167, 146, 0.08)" : "#ffffff",
                  border: isDraggingOver ? "2px dashed #B9A792" : "1px solid rgba(185, 167, 146, 0.22)",
                  borderRadius: 12,
                  padding: "16px 20px",
                  display: "flex",
                  alignItems: "center",
                  gap: 16,
                  boxShadow: "0 1px 4px rgba(0,0,0,0.03)",
                  transition: "all 0.15s ease",
                }}
              >
                {/* Reorder / Drag & Sequence Controls */}
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                  {/* Drag Handle */}
                  <div
                    title={canReorder ? "Drag to reorder section" : "Clear search to reorder"}
                    style={{
                      cursor: canReorder ? "grab" : "not-allowed",
                      color: "#B9A792",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      opacity: canReorder ? 0.75 : 0.25,
                      padding: "4px 2px",
                      userSelect: "none",
                    }}
                  >
                    <GripIcon />
                  </div>

                  {/* Section Sequence Number */}
                  <div
                    title={`Sequence Position #${sec.displayOrder ?? idx + 1}`}
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: 8,
                      flexShrink: 0,
                      background: "rgba(185, 167, 146, 0.12)",
                      border: "1px solid rgba(185, 167, 146, 0.22)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 13,
                      fontWeight: 700,
                      color: "#8F7B67",
                      userSelect: "none",
                    }}
                  >
                    {sec.displayOrder ?? idx + 1}
                  </div>

                  {/* Move Up / Move Down Arrow Buttons */}
                  <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                    <button
                      type="button"
                      className="rv-action-btn"
                      title={isFirst ? "Already at top" : "Move section up"}
                      onClick={() => moveSection(idx, idx - 1)}
                      disabled={isFirst || !canReorder || savingOrder}
                      style={{
                        width: 22,
                        height: 18,
                        padding: 0,
                        borderRadius: 4,
                        opacity: (isFirst || !canReorder) ? 0.3 : 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: (isFirst || !canReorder) ? "default" : "pointer",
                      }}
                    >
                      <span style={{ transform: "rotate(180deg)", display: "inline-flex", width: 11, height: 11 }}><ChevronIcon /></span>
                    </button>
                    <button
                      type="button"
                      className="rv-action-btn"
                      title={isLast ? "Already at bottom" : "Move section down"}
                      onClick={() => moveSection(idx, idx + 1)}
                      disabled={isLast || !canReorder || savingOrder}
                      style={{
                        width: 22,
                        height: 18,
                        padding: 0,
                        borderRadius: 4,
                        opacity: (isLast || !canReorder) ? 0.3 : 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: (isLast || !canReorder) ? "default" : "pointer",
                      }}
                    >
                      <span style={{ display: "inline-flex", width: 11, height: 11 }}><ChevronIcon /></span>
                    </button>
                  </div>
                </div>

                {/* Info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 14.5, fontWeight: 700, color: "#2A2421" }}>{sec.publicTitle}</span>
                    <span style={{
                      fontSize: 10.5,
                      fontWeight: 700,
                      padding: "2px 8px",
                      borderRadius: 20,
                      background: sec.isPublished ? "#e8f5e9" : "#fef3c7",
                      color: sec.isPublished ? "#1b6d24" : "#b45309",
                      border: sec.isPublished ? "1px solid rgba(27,109,36,0.15)" : "1px solid rgba(180,83,9,0.15)",
                    }}>
                      {sec.isPublished ? "Published" : "Draft"}
                    </span>
                    <span style={{
                      fontSize: 10.5,
                      fontWeight: 600,
                      padding: "2px 8px",
                      borderRadius: 20,
                      background: "rgba(185,167,146,0.12)",
                      color: "#736B66",
                      border: "1px solid rgba(185,167,146,0.2)",
                    }}>
                      {sectionTypeLabel(sec.sectionType)}
                    </span>
                    <span style={{
                      fontSize: 10.5,
                      fontWeight: 600,
                      padding: "2px 8px",
                      borderRadius: 20,
                      background: "rgba(197,160,89,0.14)",
                      color: "#8B6508",
                      border: "1px solid rgba(197,160,89,0.25)",
                    }}>
                      {sec.layoutType === "carousel" ? "Carousel" : sec.layoutType === "horizontal" ? "Horizontal Scroll" : "Grid"}
                    </span>
                    {sec.productCount !== undefined && (
                      <span style={{
                        fontSize: 11,
                        fontWeight: 600,
                        color: "#8F7B67",
                      }}>
                        • {sec.productCount} product{sec.productCount !== 1 ? "s" : ""}
                      </span>
                    )}
                  </div>
                  {sec.subtitle ? (
                    <p style={{ margin: "4px 0 0", fontSize: 12, color: "#736B66", lineHeight: 1.4 }}>
                      {sec.subtitle.slice(0, 90)}{sec.subtitle.length > 90 ? "…" : ""}
                    </p>
                  ) : null}
                </div>

                {/* Actions */}
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                  <button
                    type="button"
                    className="rv-btn"
                    style={{
                      padding: "6px 13px",
                      fontSize: 12,
                      fontWeight: 600,
                      background: "rgba(197, 160, 89, 0.12)",
                      color: "#8B6508",
                      border: "1px solid rgba(197, 160, 89, 0.3)",
                      borderRadius: 8,
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      cursor: "pointer",
                    }}
                    onClick={() => { setDrawer({ section: sec, defaultTab: "products" }); }}
                  >
                    <span style={{ display: "inline-flex", width: 13, height: 13 }}><CatalogueIcon /></span>
                    Manage Products
                  </button>
                  <button
                    type="button"
                    className="rv-btn rv-btn--ghost"
                    style={{ padding: "6px 12px", fontSize: 12, borderRadius: 8, display: "inline-flex", alignItems: "center", gap: 5 }}
                    onClick={() => setDrawer({ section: sec })}
                  >
                    <EditIcon /> Edit
                  </button>
                  <button
                    type="button"
                    className="rv-action-btn rv-action-btn--del"
                    title="Archive section"
                    onClick={() => handleArchive(sec.sectionID, sec.publicTitle)}
                    disabled={archiving === sec.sectionID}
                  >
                    {archiving === sec.sectionID ? <span className="rv-pm-mini-spin" /> : <TrashIcon />}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Section Editor Drawer */}
      {drawer && (
        <SectionEditorDrawer
          section={drawer.section}
          defaultTab={drawer.defaultTab}
          catalogueItems={catalogueItems}
          products={products}
          onSave={handleSaved}
          onClose={() => setDrawer(null)}
          showToast={showToast}
        />
      )}
    </div>
  );
}

/* ══════════════════════════════════════
   HOMEPAGE BUILDER TAB
══════════════════════════════════════ */
function HomepageBuilderTab({ showToast }) {
  const [allSections, setAllSections] = useState([]);
  const [savedLayout, setSavedLayout] = useState([]); // ordered sectionIDs (saved)
  const [draftLayout, setDraftLayout] = useState([]); // working copy in builder
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const dragSrcIdx = useRef(null);

  const isDirty = useMemo(() => {
    if (draftLayout.length !== savedLayout.length) return true;
    return draftLayout.some((id, i) => id !== savedLayout[i]);
  }, [draftLayout, savedLayout]);

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const [secs, layoutIds] = await Promise.all([
          catalogueSectionApi.getAll(),
          pageLayoutApi.getDraft("homepage"),
        ]);
        const secList = Array.isArray(secs) ? secs : [];
        const idList = Array.isArray(layoutIds) ? layoutIds : [];
        setAllSections(secList);
        setSavedLayout(idList);
        setDraftLayout(idList);
      } catch (err) {
        showToast(err.message || "Failed to load layout.", "error");
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [showToast]);

  /* Drag handles */
  const handleDragStart = (idx) => { dragSrcIdx.current = idx; };
  const handleDragOver = (e, idx) => { e.preventDefault(); setDragOverIdx(idx); };
  const handleDragEnd = () => { dragSrcIdx.current = null; setDragOverIdx(null); };
  const handleDrop = (e, dropIdx) => {
    e.preventDefault();
    const src = dragSrcIdx.current;
    if (src === null || src === dropIdx) { setDragOverIdx(null); return; }
    const next = [...draftLayout];
    const [moved] = next.splice(src, 1);
    next.splice(dropIdx, 0, moved);
    setDraftLayout(next);
    setDragOverIdx(null);
    dragSrcIdx.current = null;
  };

  const moveUp = (idx) => { if (idx === 0) return; const n = [...draftLayout];[n[idx - 1], n[idx]] = [n[idx], n[idx - 1]]; setDraftLayout(n); };
  const moveDown = (idx) => { if (idx === draftLayout.length - 1) return; const n = [...draftLayout];[n[idx + 1], n[idx]] = [n[idx], n[idx + 1]]; setDraftLayout(n); };

  const toggleInLayout = (sectionId) => {
    setDraftLayout((prev) =>
      prev.includes(sectionId)
        ? prev.filter((id) => id !== sectionId)
        : [...prev, sectionId]
    );
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await pageLayoutApi.saveDraft("homepage", draftLayout);
      setSavedLayout(draftLayout);
      showToast("Layout draft saved.");
    } catch (err) {
      showToast(err.message || "Save failed.", "error");
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    if (!window.confirm("Publish this layout live to the public website?")) return;
    setPublishing(true);
    try {
      await pageLayoutApi.saveDraft("homepage", draftLayout);
      await pageLayoutApi.publish("homepage");
      setSavedLayout(draftLayout);
      showToast("Homepage published live! 🎉");
    } catch (err) {
      showToast(err.message || "Publish failed.", "error");
    } finally {
      setPublishing(false);
    }
  };

  const getSec = (id) => allSections.find((s) => s.sectionID === id);
  const allNotInLayout = allSections.filter((s) => !draftLayout.includes(s.sectionID));

  const sectionTypeLabel = (t) => ({ HERO_BANNER: "Hero", CURATED_GRID: "Grid", CAROUSEL: "Carousel" }[t] ?? t);

  return (
    <div>
      {/* Action Bar */}
      <div style={{
        display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20,
        flexWrap: "wrap", gap: 12,
      }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: "#332F2F" }}>Homepage Display Order</h3>
          <p style={{ margin: "2px 0 0", fontSize: 12, color: "#736B66" }}>
            Drag sections to reorder how they appear on the public website.
          </p>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <a
            href="/catalogue"
            target="_blank"
            rel="noopener noreferrer"
            className="rv-btn rv-btn--ghost"
            style={{ textDecoration: "none", fontSize: 12.5 }}
          >
            <ExternalLinkIcon /> Preview
          </a>
          <button
            className="rv-btn rv-btn--ghost"
            onClick={handleSave}
            disabled={saving || !isDirty}
            style={{ fontSize: 12.5 }}
          >
            {saving ? <span className="rv-spinner" /> : null}
            Save
          </button>
          <button
            className="rv-btn rv-btn--primary"
            onClick={handlePublish}
            disabled={publishing}
            style={{ fontSize: 12.5 }}
            title="Makes your homepage visible to customers"
          >
            {publishing ? <span className="rv-spinner" /> : null}
            Go Live
          </button>
        </div>
      </div>

      {/* Unsaved banner */}
      {isDirty && (
        <div style={{
          background: "rgba(197,160,89,0.1)",
          border: "1px solid rgba(197,160,89,0.3)",
          borderRadius: 8, padding: "10px 16px", marginBottom: 16,
          fontSize: 12.5, color: "#8B6508", display: "flex", alignItems: "center", gap: 8,
        }}>
          ⚠️ You have unsaved layout changes. Click <strong>Save</strong> or <strong>Go Live</strong>.
        </div>
      )}

      {loading ? (
        <div className="rv-cat-pg-empty"><div className="rv-loader" /><p>Loading layout…</p></div>
      ) : (
        <div style={{ display: "flex", gap: 24, flexWrap: "wrap", alignItems: "flex-start" }}>

          {/* Live stack */}
          <div style={{ flex: "1 1 380px", minWidth: 300 }}>
            <p style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "#736B66", marginBottom: 10 }}>Homepage Order</p>
            {draftLayout.length === 0 ? (
              <p style={{ fontSize: 13, color: "#B9A792", fontStyle: "italic" }}>No sections in layout. Add sections from the right panel.</p>
            ) : (
              draftLayout.map((sectionId, idx) => {
                const sec = getSec(sectionId);
                if (!sec) return null;
                return (
                  <div
                    key={sectionId}
                    draggable
                    onDragStart={() => handleDragStart(idx)}
                    onDragOver={(e) => handleDragOver(e, idx)}
                    onDrop={(e) => handleDrop(e, idx)}
                    onDragLeave={() => setDragOverIdx(null)}
                    onDragEnd={handleDragEnd}
                    style={{
                      display: "flex", alignItems: "center", gap: 12,
                      padding: "12px 16px", marginBottom: 8,
                      borderRadius: 10,
                      border: dragOverIdx === idx ? "2px solid #B9A792" : "1px solid rgba(185,167,146,0.22)",
                      background: dragOverIdx === idx ? "rgba(185,167,146,0.1)" : "#fff",
                      cursor: "grab", transition: "all 0.15s",
                      boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
                    }}
                  >
                    <GripIcon />
                    <div style={{
                      width: 32, height: 32, borderRadius: 8,
                      background: "rgba(185,167,146,0.12)",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 12, fontWeight: 700, color: "#B9A792", flexShrink: 0,
                    }}>#{idx + 1}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: "#332F2F" }}>{sec.publicTitle}</div>
                      <div style={{ fontSize: 11, color: "#736B66" }}>{sectionTypeLabel(sec.sectionType)} · {sec.isPublished ? "Published" : "Draft"}</div>
                    </div>
                    <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
                      <button
                        className="rv-action-btn"
                        title="Move up"
                        onClick={() => moveUp(idx)}
                        disabled={idx === 0}
                        style={{ opacity: idx === 0 ? 0.35 : 1, display: "flex", alignItems: "center", justifyContent: "center" }}
                      >
                        <span style={{ transform: "rotate(180deg)", display: "inline-flex" }}><ChevronIcon /></span>
                      </button>
                      <button
                        className="rv-action-btn"
                        title="Move down"
                        onClick={() => moveDown(idx)}
                        disabled={idx === draftLayout.length - 1}
                        style={{ opacity: idx === draftLayout.length - 1 ? 0.35 : 1, display: "flex", alignItems: "center", justifyContent: "center" }}
                      >
                        <ChevronIcon />
                      </button>
                      <button
                        className="rv-action-btn rv-action-btn--del"
                        title="Remove from homepage"
                        onClick={() => toggleInLayout(sectionId)}
                      >
                        <CloseIcon />
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Available to add */}
          {allNotInLayout.length > 0 && (
            <div style={{ flex: "0 0 260px" }}>
              <p style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "#736B66", marginBottom: 10 }}>Available Sections</p>
              {allNotInLayout.map((sec) => (
                <div
                  key={sec.sectionID}
                  style={{
                    display: "flex", alignItems: "center", gap: 10,
                    padding: "10px 14px", marginBottom: 8,
                    borderRadius: 10,
                    border: "1px dashed rgba(185,167,146,0.3)",
                    background: "#FAFAF8",
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12.5, fontWeight: 600, color: "#332F2F" }}>{sec.publicTitle}</div>
                    <div style={{ fontSize: 11, color: "#736B66" }}>
                      {sectionTypeLabel(sec.sectionType)} · <span style={{ color: sec.isPublished ? "#1b6d24" : "#b45309" }}>{sec.isPublished ? "Published" : "Draft"}</span>
                    </div>
                  </div>
                  <button
                    className="rv-btn rv-btn--ghost"
                    style={{ fontSize: 11, padding: "4px 10px" }}
                    onClick={() => toggleInLayout(sec.sectionID)}
                  >
                    <PlusIcon /> Add
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}


/* ══════════════════════════════════════
   SECTION ASSIGN QUICK MODAL (TAB 1)
══════════════════════════════════════ */
function SectionAssignModal({ item, sections, onAssign, onClear, onClose }) {
  const [customText, setCustomText] = useState("");

  return (
    <div className="rv-dialog-backdrop" onClick={onClose}>
      <div
        className="rv-dialog"
        style={{ maxWidth: 440, width: "100%" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="rv-modal-header">
          <div className="rv-modal-icon"><LayersIcon /></div>
          <div>
            <h3 className="rv-modal-title">Assign Section</h3>
            <p className="rv-modal-sub" style={{ margin: "2px 0 0" }}>
              Showcase placement for <strong>{item.displayName}</strong>
            </p>
          </div>
          <button className="rv-modal-close" onClick={onClose}><CloseIcon /></button>
        </div>

        <div className="rv-modal-body" style={{ padding: "16px 20px" }}>
          <p style={{
            fontSize: 11, fontWeight: 700, color: "#736B66",
            textTransform: "uppercase", letterSpacing: "0.06em", margin: "0 0 10px"
          }}>
            Available Showcase Sections
          </p>

          <div style={{ display: "flex", flexDirection: "column", gap: 8, maxHeight: 220, overflowY: "auto" }}>
            {sections.length === 0 ? (
              <p style={{ fontSize: 12.5, color: "#B9A792", fontStyle: "italic" }}>
                No CMS sections created yet. Enter a custom name below.
              </p>
            ) : (
              sections.map((sec) => {
                const secTitle = sec.publicTitle || sec.internalName;
                const isCurrent = item.displaySection?.toLowerCase() === secTitle.toLowerCase();
                return (
                  <div
                    key={sec.sectionID}
                    onClick={() => onAssign(item, secTitle, sec.sectionID)}
                    style={{
                      display: "flex", alignItems: "center", justifyContent: "space-between",
                      padding: "9px 13px", borderRadius: 8,
                      border: isCurrent ? "2px solid #B9A792" : "1px solid rgba(185,167,146,0.22)",
                      background: isCurrent ? "rgba(185,167,146,0.12)" : "#FAFAFA",
                      cursor: "pointer", transition: "all 0.15s",
                    }}
                  >
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: "#332F2F" }}>
                        {secTitle}
                      </div>
                      {sec.subtitle && (
                        <div style={{ fontSize: 11, color: "#736B66", marginTop: 2 }}>{sec.subtitle}</div>
                      )}
                    </div>
                    <span style={{
                      fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 12,
                      background: sec.isPublished ? "#e8f5e9" : "#fef3c7",
                      color: sec.isPublished ? "#1b6d24" : "#b45309",
                    }}>
                      {sec.isPublished ? "Live" : "Draft"}
                    </span>
                  </div>
                );
              })
            )}
          </div>

          <div style={{ marginTop: 16 }}>
            <label className="rv-label" style={{ fontSize: 11.5 }}>Or Custom Section Name</label>
            <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
              <input
                className="rv-input"
                placeholder="e.g. Bridal Edit, Solitaire Festival"
                value={customText}
                onChange={(e) => setCustomText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && customText.trim()) {
                    onAssign(item, customText.trim());
                  }
                }}
              />
              <button
                type="button"
                className="rv-btn rv-btn--primary"
                onClick={() => {
                  if (customText.trim()) onAssign(item, customText.trim());
                }}
                disabled={!customText.trim()}
              >
                Set
              </button>
            </div>
          </div>
        </div>

        <div className="rv-modal-footer" style={{ display: "flex", justifyContent: "space-between" }}>
          {item.displaySection ? (
            <button
              type="button"
              className="rv-btn rv-btn--ghost"
              style={{ color: "#c0392b" }}
              onClick={() => onClear(item)}
            >
              Clear Section
            </button>
          ) : <div />}
          <button type="button" className="rv-btn rv-btn--ghost" onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}


/* ══════════════════════════════════════
   MAIN CATALOGUE PAGE
══════════════════════════════════════ */
export default function CataloguePage({ initialTab = "items", onTabChange }) {
  const [items, setItems] = useState([]);
  const [products, setProducts] = useState([]);
  const [availableSections, setAvailableSections] = useState([]);
  const [sectionAssignModal, setSectionAssignModal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterFeatured, setFilterFeatured] = useState("all");
  const [activeTab, setActiveTab] = useState(initialTab || "items"); // "items" | "sections" | "builder"

  useEffect(() => {
    if (initialTab && ["items", "sections", "builder"].includes(initialTab)) {
      setActiveTab(initialTab);
    }
  }, [initialTab]);

  const [modal, setModal] = useState(null);
  const [viewModal, setViewModal] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [toast, setToast] = useState(null);
  const [photoPanel, setPhotoPanel] = useState(null);

  /* ── Resizable columns ── */
  const tableRef = useRef(null);
  const [tableReady, setTableReady] = useState(false);
  // #, Display Name, Product, Section, Featured, Status, Photos, Actions
  useResizableColumns(tableRef, tableReady, [45, 270, 210, 130, 85, 80, 115, 85]);

  const showToast = useCallback((message, type = "success") => setToast({ message, type }), []);

  /* ── Fetch ── */
  const fetchItems = useCallback(async () => {
    try {
      setLoading(true);
      setTableReady(false);
      const [itemData, prodData, secData] = await Promise.all([
        catalogueItemApi.getAll().catch(() => []),
        productApi.getAll().catch(() => []),
        catalogueSectionApi.getAll().catch(() => []),
      ]);
      const prodList = Array.isArray(prodData) ? prodData : prodData?.data ?? [];
      setProducts(prodList);
      setItems(Array.isArray(itemData) ? itemData : itemData?.data ?? []);
      setAvailableSections(Array.isArray(secData) ? secData : []);
    } catch (err) {
      showToast(err.message || "Failed to load catalogue.", "error");
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => { fetchItems(); }, [fetchItems]);
  useEffect(() => { if (!loading) setTableReady(true); }, [loading]);

  /* ── Quick Section Assign Handlers ── */
  const handleQuickClearSection = async (item) => {
    try {
      if (item.displaySection) {
        const targetSec = availableSections.find(
          (s) => s.publicTitle === item.displaySection || s.title === item.displaySection
        );
        if (targetSec?.sectionID) {
          await catalogueSectionApi.removeProduct(
            targetSec.sectionID,
            item.catalogueItemID || item.id
          ).catch(() => { });
        }
      }
      await catalogueItemApi.update({ ...item, displaySection: "" });
      showToast(`Removed section from "${item.displayName}".`);
      setSectionAssignModal(null);
      fetchItems();
    } catch (err) {
      showToast(err.message || "Failed to remove section.", "error");
    }
  };

  const handleQuickAssignSection = async (item, sectionTitle, sectionId) => {
    try {
      const targetSec = availableSections.find(
        (s) => s.sectionID === sectionId || s.publicTitle === sectionTitle || s.title === sectionTitle
      );
      if (targetSec?.sectionID) {
        await catalogueSectionApi.assignProduct(
          targetSec.sectionID,
          item.catalogueItemID || item.id
        ).catch(() => { });
      }
      await catalogueItemApi.update({ ...item, displaySection: sectionTitle });
      showToast(`Assigned "${item.displayName}" to ${sectionTitle}.`);
      setSectionAssignModal(null);
      fetchItems();
    } catch (err) {
      showToast(err.message || "Failed to assign section.", "error");
    }
  };

  const [togglingId, setTogglingId] = useState(null);

  const handleToggleActive = async (e, item) => {
    e.stopPropagation();
    if (togglingId) return;
    const newActive = !item.isActive;
    setTogglingId(item.catalogueItemID);
    try {
      await catalogueItemApi.update({ ...item, isActive: newActive });
      setItems(prev => prev.map(it => it.catalogueItemID === item.catalogueItemID ? { ...it, isActive: newActive } : it));
      showToast(newActive ? "Item now visible online" : "Item hidden from customers");
    } catch (err) {
      showToast(err.message || "Failed to update item status.", "error");
    } finally {
      setTogglingId(null);
    }
  };

  const getProductName = (productID, item) => {
    const prod = products.find(p => Number(p.productID) === Number(productID));
    return prod?.productName || item?.displayName || (productID ? `Product #${productID}` : "Fine Jewellery Piece");
  };

  /* ── Save ── */
  const handleSave = async (formData, pendingFiles = []) => {
    setSaving(true);
    try {
      let itemID = formData.catalogueItemID;
      if (itemID) {
        await catalogueItemApi.update(formData);
      } else {
        const created = await catalogueItemApi.create(formData);
        itemID = created?.catalogueItemID ?? created?.data?.catalogueItemID;
      }

      // If a section is assigned, link relationally (auto-create draft if not exists)
      if (itemID && formData.displaySection) {
        const secName = formData.displaySection.trim();
        let targetSec = availableSections.find(
          (s) => (s.publicTitle || s.title || "").toLowerCase() === secName.toLowerCase()
        );

        if (!targetSec) {
          try {
            const generatedSlug = secName
              .toLowerCase()
              .replace(/[^a-z0-9]+/g, "-")
              .replace(/^-+|-+$/g, "");
            const newSectionData = {
              title: secName,
              slug: generatedSlug || `section-${Date.now()}`,
              sectionType: "CURATED_GRID",
              layoutTemplate: "Grid",
              placement: "HOMEPAGE",
              isPublished: false,
              isArchived: false,
            };
            targetSec = await catalogueSectionApi.create(newSectionData);
            if (targetSec?.sectionID) {
              setAvailableSections(prev => [...prev, targetSec]);
            }
          } catch (autoErr) {
            console.warn("Auto-create section failed, skipping section assignment:", autoErr);
          }
        }

        if (targetSec?.sectionID) {
          await catalogueSectionApi.assignProduct(
            targetSec.sectionID,
            itemID
          ).catch(() => { });
        }
      }

      // If photos were dropped/selected, upload them right away
      if (pendingFiles.length > 0 && itemID) {
        await cataloguePhotoApi.upload({
          catalogueItemID: itemID,
          files: pendingFiles,
          comments: "",
          displayOrder: 1,
          isPrimary: true,
        });
      }

      showToast(formData.catalogueItemID ? "Catalogue item updated." : "Catalogue item & photos created successfully.");
      setModal(null);
      fetchItems();
    } catch (err) {
      showToast(err.message || "Something went wrong.", "error");
    } finally {
      setSaving(false);
    }
  };

  /* ── Delete ── */
  const handleDelete = async () => {
    if (!confirm) return;
    try {
      await catalogueItemApi.remove(confirm.id);
      showToast("Catalogue item deleted.");
      setConfirm(null);
      fetchItems();
    } catch (err) {
      showToast(err.message || "Delete failed.", "error");
    }
  };

  /* ── Filtered list ── */
  const filtered = items.filter(item => {
    const prodName = getProductName(item.productID);
    const matchSearch =
      item.displayName?.toLowerCase().includes(search.toLowerCase()) ||
      item.displaySection?.toLowerCase().includes(search.toLowerCase()) ||
      prodName.toLowerCase().includes(search.toLowerCase());
    const matchStatus =
      filterStatus === "all" ||
      (filterStatus === "active" && item.isActive) ||
      (filterStatus === "inactive" && !item.isActive);
    const matchFeatured =
      filterFeatured === "all" ||
      (filterFeatured === "featured" && item.isFeatured) ||
      (filterFeatured === "unfeatured" && !item.isFeatured);
    return matchSearch && matchStatus && matchFeatured;
  });

  return (
    <div className="rv-cat-pg">

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      {confirm && (
        <ConfirmDialog
          name={confirm.name}
          onConfirm={handleDelete}
          onCancel={() => setConfirm(null)}
        />
      )}
      {modal && (
        <CatalogueModal
          initial={modal.data}
          products={products}
          sections={availableSections}
          onSave={handleSave}
          onClose={() => setModal(null)}
          saving={saving}
          showToast={showToast}
        />
      )}
      {viewModal && (
        <CatalogueDetailModal
          item={viewModal}
          products={products}
          onEdit={(it) => setModal({ data: it })}
          onManagePhotos={(it) => setPhotoPanel(it)}
          onClose={() => setViewModal(null)}
          showToast={showToast}
        />
      )}
      {photoPanel && (
        <PhotoPanel
          item={photoPanel}
          onClose={() => setPhotoPanel(null)}
          showToast={showToast}
        />
      )}
      {sectionAssignModal && (
        <SectionAssignModal
          item={sectionAssignModal}
          sections={availableSections}
          onAssign={handleQuickAssignSection}
          onClear={handleQuickClearSection}
          onClose={() => setSectionAssignModal(null)}
        />
      )}

      {/* ── Topbar ── */}
      <div className="rv-cat-pg-topbar">
        <div className="rv-cat-pg-topbar-left">
          <h2 className="rv-cat-pg-heading">
            {activeTab === "sections" ? "Manage Sections" : activeTab === "builder" ? "Homepage Builder" : "Catalogue Items"}
          </h2>
          {activeTab === "items" && <span className="rv-cat-pg-count">{items.length} items</span>}
        </div>
        <div className="rv-cat-pg-topbar-actions">
          <a
            href="/catalogue"
            target="_blank"
            rel="noopener noreferrer"
            className="rv-btn rv-btn--ghost"
            style={{ textDecoration: "none" }}
            title="Open customer-facing public showcase in a new tab"
          >
            <ExternalLinkIcon /> <span>View Showcase</span>
          </a>
          {activeTab === "items" && (
            <button className="rv-btn rv-btn--primary" onClick={() => setModal({ data: {} })}>
              <PlusIcon /> <span>New Item</span>
            </button>
          )}
        </div>
      </div>

      {/* ══ TAB: MANAGE SECTIONS ══ */}
      {activeTab === "sections" && (
        <div className="rv-card rv-cat-pg-card" style={{ padding: 24 }}>
          <ManageSectionsTab catalogueItems={items} products={products} showToast={showToast} />
        </div>
      )}

      {/* ══ TAB: HOMEPAGE BUILDER ══ */}
      {activeTab === "builder" && (
        <div className="rv-card rv-cat-pg-card" style={{ padding: 24 }}>
          <HomepageBuilderTab showToast={showToast} />
        </div>
      )}

      {/* ══ TAB: CATALOGUE ITEMS (existing) ══ */}
      {activeTab === "items" && (<>

        {/* ── Summary KPI Cards ── */}
        {!loading && items.length > 0 && (
          <div className="rv-cat-pg-stats">
            {/* Total Items */}
            <div
              className={`rv-cat-pg-stat ${(filterStatus === "all" && filterFeatured === "all") ? "rv-cat-pg-stat--active-filter" : ""}`}
              onClick={() => { setFilterStatus("all"); setFilterFeatured("all"); }}
              title="Click to reset filters and view all catalogue items"
            >
              <div className="rv-cat-pg-stat-icon-wrap" style={{ background: "rgba(185,167,146,0.18)", color: "var(--taupe-dark)" }}>
                <CatalogueIcon />
              </div>
              <div className="rv-cat-pg-stat-body">
                <span className="rv-cat-pg-stat-label">Total Portfolio</span>
                <span className="rv-cat-pg-stat-val">{items.length}</span>
                <span className="rv-cat-pg-stat-sub">All catalogue designs</span>
              </div>
            </div>

            {/* Active Items */}
            <div
              className={`rv-cat-pg-stat ${filterStatus === "active" ? "rv-cat-pg-stat--active-filter" : ""}`}
              onClick={() => setFilterStatus(s => s === "active" ? "all" : "active")}
              title="Click to toggle Active showcase items"
            >
              <div className="rv-cat-pg-stat-icon-wrap" style={{ background: "rgba(39, 174, 96, 0.12)", color: "#27ae60" }}>
                <EyeIcon />
              </div>
              <div className="rv-cat-pg-stat-body">
                <span className="rv-cat-pg-stat-label">Active Showcase</span>
                <span className="rv-cat-pg-stat-val">{items.filter(i => i.isActive).length}</span>
                <span className="rv-cat-pg-stat-sub">Live &amp; visible to customers</span>
              </div>
            </div>

            {/* Featured Highlights */}
            <div
              className={`rv-cat-pg-stat ${filterFeatured === "featured" ? "rv-cat-pg-stat--active-filter" : ""}`}
              onClick={() => setFilterFeatured(f => f === "featured" ? "all" : "featured")}
              title="Click to toggle Featured highlights"
            >
              <div className="rv-cat-pg-stat-icon-wrap" style={{ background: "rgba(212, 175, 55, 0.15)", color: "#B8860B" }}>
                <StarIcon />
              </div>
              <div className="rv-cat-pg-stat-body">
                <span className="rv-cat-pg-stat-label">Featured Pieces</span>
                <span className="rv-cat-pg-stat-val">{items.filter(i => i.isFeatured).length}</span>
                <span className="rv-cat-pg-stat-sub">Highlighted hero jewellery</span>
              </div>
            </div>

            {/* Curated Sections */}
            <div
              className="rv-cat-pg-stat"
              style={{ cursor: "default" }}
              title="Showcase collection sections"
            >
              <div className="rv-cat-pg-stat-icon-wrap" style={{ background: "rgba(143, 123, 103, 0.15)", color: "#8F7B67" }}>
                <LayersIcon />
              </div>
              <div className="rv-cat-pg-stat-body">
                <span className="rv-cat-pg-stat-label">Curated Sections</span>
                <span className="rv-cat-pg-stat-val">{new Set(items.map(i => i.displaySection).filter(Boolean)).size}</span>
                <span className="rv-cat-pg-stat-sub">Bridal, Deluxe, Solitaire…</span>
              </div>
            </div>
          </div>
        )}

        {/* ── Filters ── */}
        <div className="rv-cat-pg-filters">
          <div className="rv-cat-pg-search">
            <SearchIcon />
            <input
              placeholder="Search by name, section or product…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            {search && <button className="rv-cat-pg-search-clear" onClick={() => setSearch("")}><CloseIcon /></button>}
          </div>

          <div className="rv-filter-tabs">
            {["all", "active", "inactive"].map(s => (
              <button key={s} className={`rv-filter-tab ${filterStatus === s ? "rv-filter-tab--active" : ""}`} onClick={() => setFilterStatus(s)}>
                {s.charAt(0).toUpperCase() + s.slice(1)}
              </button>
            ))}
          </div>

          <div className="rv-filter-tabs">
            {[
              { key: "all", label: "All" },
              { key: "featured", label: "Featured", icon: <StarIcon /> },
              { key: "unfeatured", label: "Standard" },
            ].map(({ key, label, icon }) => (
              <button key={key} className={`rv-filter-tab ${filterFeatured === key ? "rv-filter-tab--active" : ""}`} onClick={() => setFilterFeatured(key)}>
                {icon && <span style={{ display: "inline-flex", alignItems: "center", marginRight: 4 }}>{icon}</span>}
                {label}
              </button>
            ))}
          </div>
        </div>


        {/* ── Table card ── */}
        <div className="rv-card rv-cat-pg-card">
          {/* Desktop Table View */}
          <div className="rv-desktop-table-view">
            {loading ? (
              <div className="rv-cat-pg-empty">
                <div className="rv-loader" /><p>Loading catalogue…</p>
              </div>
            ) : filtered.length === 0 ? (
              <div className="rv-cat-pg-empty">
                <div className="rv-empty-icon"><CatalogueIcon /></div>
                <p className="rv-empty-title">No catalogue items found</p>
                <p className="rv-empty-sub">{search ? "Try a different search term." : "Add your first catalogue item to get started."}</p>
                {!search && (
                  <button className="rv-btn rv-btn--primary rv-mt-12" onClick={() => setModal({ data: {} })}>
                    <PlusIcon /> New Item
                  </button>
                )}
              </div>
            ) : (
              <div className="rv-table-wrap rv-cat-pg-table-wrap">
                <table className="rv-table rv-cat-pg-table" ref={tableRef}>
                  <thead>
                    <tr>
                      <th style={{ width: 45, textAlign: "center" }}>#</th>
                      <th>Display Name</th>
                      <th>Product</th>
                      <th style={{ width: 130 }}>Section</th>
                      <th style={{ width: 85, textAlign: "center" }}>Featured</th>
                      <th style={{ width: 80, textAlign: "center" }}>Status</th>
                      <th style={{ width: 115, textAlign: "center" }}>Photos</th>
                      <th style={{ width: 85, textAlign: "center" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((item, i) => (
                      <tr
                        key={item.catalogueItemID}
                        className="rv-cat-pg-row"
                        onClick={() => setViewModal(item)}
                        style={{ cursor: "pointer" }}
                        title="Click to view details"
                      >
                        <td className="rv-cat-pg-idx">{i + 1}</td>
                        <td>
                          <div className="rv-cat-pg-name-cell">
                            <div className="rv-cat-pg-dot" />
                            <div>
                              <span
                                className="rv-cat-pg-name"
                                title="Click to view full details"
                              >
                                {item.displayName}
                              </span>
                              {item.descriptionLbl && (
                                <span className="rv-cat-pg-sublabel">{item.descriptionLbl}</span>
                              )}
                              {item.comments && (
                                <span
                                  style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11, color: "var(--taupe)", marginTop: 2 }}
                                  title={`Internal Comment: ${item.comments}`}
                                >
                                  <CommentIcon /> {item.comments.length > 40 ? item.comments.slice(0, 40) + "…" : item.comments}
                                </span>
                              )}
                            </div>
                          </div>
                        </td>
                        <td>
                          {(() => {
                            const prod = products.find(p => Number(p.productID) === Number(item.productID));
                            return (
                              <div>
                                <span className="rv-cat-pg-product">{prod?.productName || item.displayName || `Product #${item.productID}`}</span>
                                {prod?.productCode && (
                                  <div style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 3 }}>
                                    <span style={{ fontSize: 11, fontWeight: 700, fontFamily: "monospace", color: "#8F7B67", background: "rgba(185,167,146,0.14)", padding: "1px 5px", borderRadius: 4 }}>
                                      {prod.productCode}
                                    </span>
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        navigator.clipboard?.writeText(prod.productCode);
                                        showToast(`Copied code: ${prod.productCode}`);
                                      }}
                                      title={`Copy code ${prod.productCode}`}
                                      style={{ background: "none", border: "none", cursor: "pointer", padding: "1px 3px", color: "var(--taupe)", display: "inline-flex", alignItems: "center" }}
                                    >
                                      <CopyIcon />
                                    </button>
                                  </div>
                                )}
                              </div>
                            );
                          })()}
                        </td>
                        <td>
                          {item.displaySection ? (
                            <span
                              className="rv-section-badge"
                              title={`${item.displaySection} — click to change section`}
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 6,
                                padding: "3px 8px",
                                cursor: "pointer",
                                userSelect: "none",
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                setSectionAssignModal(item);
                              }}
                            >
                              <span>{item.displaySection}</span>
                              <span
                                title="Clear section"
                                role="button"
                                tabIndex={0}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleQuickClearSection(item);
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter" || e.key === " ") {
                                    e.stopPropagation();
                                    handleQuickClearSection(item);
                                  }
                                }}
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  width: 14,
                                  height: 14,
                                  borderRadius: "50%",
                                  background: "rgba(185,167,146,0.3)",
                                  color: "#555",
                                  fontSize: 10,
                                  fontWeight: 700,
                                  lineHeight: 1,
                                  cursor: "pointer",
                                  marginLeft: 2,
                                }}
                              >
                                ✕
                              </span>
                            </span>
                          ) : (
                            <button
                              type="button"
                              className="rv-btn rv-btn--ghost"
                              style={{
                                fontSize: 11,
                                padding: "3px 8px",
                                borderStyle: "dashed",
                                borderRadius: 12,
                                color: "#B9A792",
                                fontWeight: 600,
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                setSectionAssignModal(item);
                              }}
                              title="Assign to a showcase section"
                            >
                              + Assign Section
                            </button>
                          )}
                        </td>
                        <td>
                          {item.isFeatured
                            ? <span className="rv-featured-badge"><StarIcon /> Featured</span>
                            : <span className="rv-empty-val">—</span>}
                        </td>
                        <td>
                          <button
                            type="button"
                            onClick={(e) => handleToggleActive(e, item)}
                            disabled={togglingId === item.catalogueItemID}
                            title={item.isActive ? "Click to hide from store showcase" : "Click to publish to store showcase"}
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 6,
                              padding: "4px 12px",
                              borderRadius: "20px",
                              fontSize: "11px",
                              fontWeight: 600,
                              letterSpacing: "0.03em",
                              cursor: togglingId === item.catalogueItemID ? "wait" : "pointer",
                              userSelect: "none",
                              background: item.isActive ? "rgba(16, 185, 129, 0.1)" : "rgba(160, 120, 80, 0.08)",
                              color: item.isActive ? "#065F46" : "#7A532B",
                              border: item.isActive ? "1px solid rgba(16, 185, 129, 0.3)" : "1px solid rgba(160, 120, 80, 0.24)",
                              transition: "all 0.18s ease",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {togglingId === item.catalogueItemID ? (
                              <span
                                className="rv-spinner"
                                style={{
                                  width: 8,
                                  height: 8,
                                  borderWidth: "1.5px",
                                  borderColor: item.isActive ? "#065F46 transparent #065F46 transparent" : "#7A532B transparent #7A532B transparent",
                                  margin: "auto",
                                }}
                              />
                            ) : (
                              <span
                                style={{
                                  width: 6.5,
                                  height: 6.5,
                                  borderRadius: "50%",
                                  background: item.isActive ? "#10B981" : "#A07850",
                                  boxShadow: item.isActive ? "0 0 0 2.5px rgba(16, 185, 129, 0.22)" : "none",
                                  flexShrink: 0,
                                }}
                              />
                            )}
                            {item.isActive ? "Live" : "Hidden"}
                          </button>
                        </td>
                        <td>
                          <button
                            className="rv-photo-chip"
                            onClick={(e) => { e.stopPropagation(); setPhotoPanel(item); }}
                            title="Upload, view or manage photos for this item"
                            style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "5px 10px", fontSize: 11.5, fontWeight: 600 }}
                          >
                            <ImageIcon /> Manage Photos
                          </button>
                        </td>
                        <td>
                          <div className="rv-actions" style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
                            
                            <button
                              className="rv-action-btn"
                              title="View Full Details"
                              onClick={() => setViewModal(item)}
                              style={{ color: "var(--taupe-dark)" }}
                            >
                              <EyeIcon />
                            </button>
                            {/* Showcase & Copy link actions commented out to conserve space
                            <a
                              href={`/catalogue?item=${item.catalogueItemID}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="rv-action-btn"
                              title="Open this item directly in public customer showcase"
                              style={{ color: "var(--taupe-dark)", display: "flex", alignItems: "center", justifyContent: "center" }}
                            >
                              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /><polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" /></svg>
                            </a>
                            <button
                              className="rv-action-btn"
                              title="Copy shareable link for this item"
                              onClick={() => {
                                const url = `${window.location.origin}/catalogue?item=${item.catalogueItemID}`;
                                navigator.clipboard?.writeText(url);
                                showToast("Customer showcase link copied!");
                              }}
                              style={{ color: "var(--taupe-dark)" }}
                            >
                              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" /></svg>
                            </button>
                            */}
                            <button className="rv-action-btn rv-action-btn--edit" title="Edit Item Details" onClick={() => setModal({ data: item })}><EditIcon /></button>
                            <button className="rv-action-btn rv-action-btn--del" title="Delete" onClick={() => setConfirm({ id: item.catalogueItemID, name: item.displayName })}><TrashIcon /></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Mobile Cards View */}
          <div className="rv-mobile-cards-view">
            <RvDataCardList
              loading={loading}
              items={filtered}
              emptyIcon={<CatalogueIcon />}
              emptyTitle="No catalogue items found"
              emptySubtitle={search ? "Try a different search term." : "Add your first catalogue item to get started."}
              emptyAction={
                !search && (
                  <button className="rv-btn rv-btn--primary" onClick={() => setModal({ data: {} })}>
                    <PlusIcon /> New Item
                  </button>
                )
              }
              renderCard={(item, i) => {
                const prod = products.find(p => Number(p.productID) === Number(item.productID));
                const resolvedName = prod?.productName || item.displayName || `Product #${item.productID}`;
                return (
                  <RvDataCard
                    key={item.catalogueItemID}
                    index={i + 1}
                    title={item.displayName}
                    subtitle={resolvedName}
                    badge={
                      <span
                        onClick={(e) => handleToggleActive(e, item)}
                        title="Click to toggle status"
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 6,
                          padding: "3px 9px",
                          borderRadius: "20px",
                          fontSize: "10.5px",
                          fontWeight: 600,
                          cursor: "pointer",
                          background: item.isActive ? "rgba(16, 185, 129, 0.09)" : "rgba(239, 68, 68, 0.08)",
                          color: item.isActive ? "#065F46" : "#991B1B",
                          border: item.isActive ? "1px solid rgba(16, 185, 129, 0.28)" : "1px solid rgba(239, 68, 68, 0.25)",
                        }}
                      >
                        <span
                          style={{
                            width: 6,
                            height: 6,
                            borderRadius: "50%",
                            background: item.isActive ? "#10B981" : "#EF4444",
                            boxShadow: item.isActive ? "0 0 0 2px rgba(16, 185, 129, 0.2)" : "0 0 0 2px rgba(239, 68, 68, 0.2)",
                          }}
                        />
                        {item.isActive ? "Live" : "Hidden"}
                      </span>
                    }
                    tag={
                      item.isFeatured ? (
                        <span className="rv-featured-badge" style={{ fontSize: 10.5 }}>
                          <StarIcon /> Featured
                        </span>
                      ) : null
                    }
                    fields={[
                      { label: "Product", value: resolvedName },
                      {
                        label: "Section",
                        value: item.displaySection ? (
                          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                            <span
                              className="rv-section-badge"
                              style={{ cursor: "pointer", fontSize: 11 }}
                              onClick={(e) => { e.stopPropagation(); setSectionAssignModal(item); }}
                            >
                              {item.displaySection}
                            </span>
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); handleQuickClearSection(item); }}
                              style={{ border: "none", background: "none", cursor: "pointer", color: "#888", fontSize: 11, padding: 0 }}
                              title="Clear section"
                            >
                              ✕
                            </button>
                          </span>
                        ) : (
                          <button
                            type="button"
                            className="rv-btn rv-btn--ghost"
                            style={{ fontSize: 10.5, padding: "2px 6px", borderStyle: "dashed", borderRadius: 10 }}
                            onClick={(e) => { e.stopPropagation(); setSectionAssignModal(item); }}
                          >
                            + Assign
                          </button>
                        )
                      },
                      ...(item.descriptionLbl ? [{ label: "Description", value: item.descriptionLbl, fullWidth: true }] : []),
                    ]}
                    onClick={() => setViewModal(item)}
                    actions={
                      <div className="rv-actions" style={{ display: "flex", alignItems: "center", gap: 6 }} onClick={e => e.stopPropagation()}>
                        <button
                          className="rv-photo-chip"
                          onClick={() => setPhotoPanel(item)}
                          title="Upload or manage photos"
                          style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "4px 8px", fontSize: 11, fontWeight: 600 }}
                        >
                          <ImageIcon /> Photos
                        </button>
                        <button
                          className="rv-action-btn"
                          title="View Details"
                          onClick={() => setViewModal(item)}
                          style={{ color: "var(--taupe-dark)" }}
                        >
                          <EyeIcon />
                        </button>
                        <button
                          className="rv-action-btn rv-action-btn--edit"
                          title="Edit"
                          onClick={() => setModal({ data: item })}
                        >
                          <EditIcon />
                        </button>
                        <button
                          className="rv-action-btn rv-action-btn--del"
                          title="Delete"
                          onClick={() => setConfirm({ id: item.catalogueItemID, name: item.displayName })}
                        >
                          <TrashIcon />
                        </button>
                      </div>
                    }
                  />
                );
              }}
            />
          </div>
        </div>

        {!loading && filtered.length > 0 && (
          <p className="rv-resize-hint rv-desktop-table-view"><GripIcon /> Drag column borders to resize</p>
        )}

      </>)} {/* end activeTab === "items" */}

      {/* Drawer animation keyframe */}
      <style>{`
        @keyframes rv-drawer-slide-in {
          from { transform: translateX(100%); opacity: 0.6; }
          to   { transform: translateX(0);    opacity: 1; }
        }
      `}</style>
    </div>
  );
}

/* ── Icons ── */
function PlusIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>; }
function SearchIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>; }
function EditIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>; }
function TrashIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6" /><path d="M14 11v6" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" /></svg>; }
function CloseIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>; }
function CheckIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12" /></svg>; }
function AlertIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>; }
function ChevronIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 12 15 18 9" /></svg>; }
function UploadIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><polyline points="16 16 12 12 8 16" /><line x1="12" y1="12" x2="12" y2="21" /><path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3" /></svg>; }
function ImageIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><polyline points="21 15 16 10 5 21" /></svg>; }
function StarIcon() { return <svg viewBox="0 0 24 24" fill="currentColor" stroke="none" style={{ width: 11, height: 11 }}><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" /></svg>; }
function CatalogueIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="14" y="14" width="7" height="7" /><rect x="3" y="14" width="7" height="7" /></svg>; }
function GripIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" style={{ width: 13, height: 13 }}><circle cx="9" cy="5" r="1" fill="currentColor" /><circle cx="9" cy="12" r="1" fill="currentColor" /><circle cx="9" cy="19" r="1" fill="currentColor" /><circle cx="15" cy="5" r="1" fill="currentColor" /><circle cx="15" cy="12" r="1" fill="currentColor" /><circle cx="15" cy="19" r="1" fill="currentColor" /></svg>; }
function EyeIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" style={{ width: 14, height: 14 }}><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>; }
function LayersIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" style={{ width: 18, height: 18 }}><polygon points="12 2 2 7 12 12 22 7 12 2" /><polyline points="2 17 12 22 22 17" /><polyline points="2 12 12 17 22 12" /></svg>; }
function CopyIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" width="13" height="13" style={{ display: "inline-block", verticalAlign: "middle" }}>
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  );
}
function ExternalLinkIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" width="13" height="13" style={{ display: "inline-block", verticalAlign: "middle" }}>
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
      <polyline points="15 3 21 3 21 9" />
      <line x1="10" y1="14" x2="21" y2="3" />
    </svg>
  );
}
function DiamondIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" width="36" height="36" style={{ display: "inline-block", margin: "0 auto" }}>
      <path d="M6 3h12l4 7-10 11L2 10l4-7z" />
      <path d="M2 10h20" />
      <path d="M10 3l-3 7 5 11 5-11-3-7" />
    </svg>
  );
}
function TagIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" width="13" height="13" style={{ display: "inline-block", verticalAlign: "middle", flexShrink: 0 }}>
      <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
      <line x1="7" y1="7" x2="7.01" y2="7" />
    </svg>
  );
}
function CommentIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" width="13" height="13" style={{ display: "inline-block", verticalAlign: "middle", flexShrink: 0 }}>
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  );
}